module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('section', {
    staticClass: "div_autre",
    attrs: {
      "epub:type": "preamble"
    }
  }, [_c('h2', {
    staticClass: "fmh",
    attrs: {
      "id": "ch5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/9781789742671_EPUB5#ch5"
    }
  }, [_vm._v("INTRODUCTION")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The letter to the Philippians is Paul’s warm and celebratory expression of his affection and concern for his sisters and brothers in Christ in Philippi. The church at Philippi is one that Paul and his fellow itinerant ministers had been instrumental in founding, probably over a decade before he pens this letter to them. In it, he assures these dear friends that his ministry of the gospel and joy in the Lord are unhindered, in spite of being ‘in chains’ and so far away from them. His deep desire for this congregation is that they live out their unity in Christ with one another by practising the countercultural virtue of humility. As they encounter any suffering for their singular loyalty to Jesus their Messiah and Lord, this unity will cause them to ‘stand firm in the Lord’ (4:1). Throughout Philippians, Paul holds up a number of exemplars or models for these believers, with Jesus being the model par excellence who renounced his status advantages and humbled himself to show concern for ‘the interests of [the] others’ (2:4). The letter to the Philippians, though relatively brief, includes a rich tapestry of theological and pastoral motifs that encourage the church at Philippi to embrace knowing Christ more deeply and to adopt his mindset for their communal life together.")]), _vm._v(" "), _c('h2', {
    staticClass: "ahd",
    attrs: {
      "id": "rch1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/9781789742671_EPUB5#ch6"
    }
  }, [_c('strong', {
    staticClass: "bold"
  }, [_vm._v("1. Hermeneutical considerations")])])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Exegetical commentaries do not, as a rule, include any kind of extended discussion of their hermeneutical vantage point."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-106-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-106"
    }
  }, [_vm._v("1")])])]), _vm._v(" This may simply be due to the expectation that an exegetical commentary will address, as a matter of course, historical enquiry, literary (including linguistic, grammatical, syntactical and discourse) analysis and theological assessment (i.e. the author’s distinctive point of view on matters of faith). Yet any savvy reader of the many commentaries on Philippians will soon discover that each commentator exemplifies a particular exegetical and hermeneutical method, with accompanying underlying, though often unstated, assumptions. While there is often much in common on a macro level (e.g. the shared assumption that a certain amount of historical reconstruction is necessary for understanding Philippians), inevitably there are distinctions among the commentaries on a whole range of hermeneutical matters. For example, one can detect how important any particular commentator considers a word’s history to be for determining its meaning in Philippians by a quick look at the space allotted to diachronic analysis – that is, a word’s development over time.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("My goal in this initial section is to make explicit my method for approaching Philippians. Along with my fellow commentators on Philippians, I follow the exegetical practice of a close reading of the text of Philippians, coupled with historical reconstruction of the situations of Paul and the Philippians around the time of the letter’s composition. Careful attention to the historical and literary facets of the letter leads to an understanding of Paul’s theology in Philippians, which is both pastoral and practical."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-105-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-105"
    }
  }, [_vm._v("2")])])]), _vm._v(" Paul’s theological reflection in Philippians centres on God’s work in Christ to bring salvation to humanity. His theology issues in exhortations for the community of faith to live out in the present their already granted salvation in the light of their future hope. Given this general sense of how history, literature and theology function together in Philippians, I explore in this section my reflections on the interpretive issues related to the Philippians’ situation, Paul’s authorship, Paul’s language and Paul’s theology.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("a. Reading the Philippians’ situation")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("To understand Philippians well, contemporary readers will need to reconstruct something of the situation of the Philippians – their cultural context and their church setting (see 2b). While such historical reconstruction always involves ‘approximating’ the Philippians’ context,"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-104-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-104"
    }
  }, [_vm._v("3")])])]), _vm._v(" historical work is essential for understanding the letter well. Since every reading supplies a context, the question is not whether an interpreter will sketch a backdrop for Philippians, it is whether he or she will do so intentionally with the benefit of solid historical evidence, or instead assume a backdrop apart from careful historical analysis.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("An initial question for reconstructing the Philippians’ setting involves the specific sources to be used. The letter is, of course, a primary resource, especially for considering the implied audience – the Philippian audience we discern from the contours of the letter itself. Evidence from outside the letter would then support internal evidence in corroboratory fashion. Evidence external to the letter itself could include (1) Paul’s other letters, with a recognition of the rhetorical and contingent nature of those writings (e.g. 1 Thess. 2:1–2; 2 Cor. 8:1–5); (2) archaeological and inscriptional evidence from ancient Philippi; (3) evidence from other writers of the time, with the proviso that these authors represent particular points of view. The book of Acts provides a highly relevant example of the last category (given its focus on Paul’s ministry) and fits the genre of ancient historiography. Judicious use of Acts 16, the Lukan account of how the church at Philippi began, can helpfully corroborate and augment what we hear in the letter to the Philippians."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-103-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-103"
    }
  }, [_vm._v("4")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In the work of historical reconstruction, a balanced approach is important – one that avoids the ends of a spectrum from under-constructing to over-constructing the setting. "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Under-constructing")]), _vm._v(" ignores the need for deducing facets of the original setting, often with the assumption that all that is needed is a ‘plain reading’ of the text. Yet, since every reading necessarily presumes or supplies a setting for that reading, there is no such thing as context-less interpretation. As Bockmuehl aptly observes, ‘It is too early to risk forgetting that many of this past century’s gravest injustices have been the consequence of callously ahistorical and unhistorical misappropriations of texts for ideological purposes.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-102-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-102"
    }
  }, [_vm._v("5")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The other side of the spectrum involves a tendency to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("over-construct")]), _vm._v(" the historical setting. By this term, I refer to deducing particulars of the original setting from textual details apart from consistent or sustained evidence. For example, to assume that the Philippians have an issue with grumbling because Paul calls them to refrain from grumbling (2:14) probably goes beyond the evidence across the letter, especially in the light of ‘grumbling’ as a likely echo from the Old Testament testimony of Israel’s wilderness journeying, used in Philippians 2:14–16 where a number of such echoes occur (see comment on 2:14).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("A challenging area for reconstruction in Philippians is the identity of ‘opponents’ Paul references quite briefly across the letter (1:28; 3:2; 3:18–19; see 2f). A key question is whether these references point to one, two or three distinct groups influencing the Philippians negatively in some way. Another question is how much these brief and fairly oblique references should be read in the light of Pauline opponents in other letters of his (e.g. Galatians, 1 and 2 Corinthians).")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("b. Reading Paul aided by the ‘implied author’")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("A crucial interpretive issue of the authorship of Philippians in relation to other Pauline letters is how much, if at all, these other writings should have an impact on our understanding of Philippians. It might seem obvious that it would be advantageous to study Paul’s other letters to help understand Paul’s language, thinking and theology. Yet the question of Pauline authorship of the thirteen canonical letters ascribed to Paul muddies the waters. Are only the six other ‘assured’ letters from Paul’s pen relevant for assisting in interpretation of Philippians? Or can we draw from Ephesians, Colossians and 2 Thessalonians as well? What about the ‘pastorals’? And does it matter whether the interpreter sees Paul’s theology as remaining stable over the course of his letters, or whether Paul’s theology develops over time (across his letters)?")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Given this thorny set of issues, I have found it helpful to draw on the literary concept of the implied author for setting and weighting priorities for studying Philippians (or any other individual letter). This concept identifies a discrete, textually derived portrait of Paul in each of his letters, suggesting that there is value in delineating that persona as a primary task for interpreting the letter at hand."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-101-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-101"
    }
  }, [_vm._v("6")])])]), _vm._v(" This would set out as a first and primary endeavour sketching the persona of Paul and his message derived from Philippians, with assistance from his other letters (1) to provide important historical information to fill in gaps in Philippians (similar to using Acts 16 to augment historical reconstruction for understanding Philippians); and (2) as a secondary task to understand better Paul’s thinking and practice (e.g. from literary and theological angles). The latter task tends to move beyond consideration of Philippians to reflect on either (or both) the ‘historical Paul’ or the ‘canonical Paul’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-100-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-100"
    }
  }, [_vm._v("7")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("One strategy for focusing on the implied author (the implied Paul) of Philippians involves discerning how the Philippian recipients of the letter may have ‘experienced’ Paul as they heard the letter read aloud in their gatherings. Bringing their previous relationship to and experience of Paul to that reading, their sense of Paul from the letter would have built upon the Paul they knew. We have no indication that these Philippian believers would have had access to any of Paul’s previous letters, so we should be cautious about reading the ‘implied author’ of other letters into Philippians. It is important to strike a balance between utilizing other letters for historical reconstruction and literary analysis, and steering clear of imposing the characterization of Paul derived from other letters on the implied Paul of Philippians. As Bockmuehl suggests, ‘Physical or mental icons of Paul are invariably present whenever anyone reads or hears any of his letters, while at the same time our view of the apostle is distinctively coloured by each document.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-099-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-099"
    }
  }, [_vm._v("8")])])]), _vm._v(" The goal is to allow Philippians to lead the way for understanding the Pauline ‘icon’ that emerges from it.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("When the Paul of Philippians is unduly coloured by readings of Paul from his other letters, a skewed portrait can emerge. For example, Romans and Galatians (along with the Corinthian correspondence) have exerted significant influence on Pauline studies and the church’s appropriation of Paul. As a result, Paul is sometimes viewed as a polemicist, not afraid to speak bluntly and often lacking in warmth and tact. Some interpreters have tapped into this sort of Pauline portrait for Philippians, reconstructed from the internal evidence of disunity among the Philippians as well as from potential hints of discord between Paul and this church surmised from 2:25–30 and 4:10–20. As Osiek observes (though disavows), ‘some commentators [see] conflict everywhere in the letter’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-098-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-098"
    }
  }, [_vm._v("9")])])]), _vm._v(" Yet these possible clues about relational problems between Paul and the Philippians pale in comparison with the explicit and sustained evidence of the loyal and warm relationship that pervades the letter. As I will suggest in this commentary, Paul writes to the Philippians ‘in almost wholly commendable terms’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-097-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-097"
    }
  }, [_vm._v("10")])])])]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("c. Reading Paul’s language")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("New Testament studies of the past sixty years has seen a shift in emphasis from attention on individual words to a focus on larger segments and whole discourses. Since the seminal work of James Barr in his "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Semantics of Biblical Language ")]), _vm._v("(1961), scholars have come to recognize that meaning does not adhere in individual words but in their contexts, immediate and extended. In this light, the interpretive process very much resembles a hermeneutic circle or spiral, in which the whole (a discourse) informs the parts (individual words) and the parts inform the whole. In this commentary, any focus on particular words and their meanings will be assessed within the wider contexts in which a word occurs.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Increased attention to linguistics informs the way we understand how words work."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-096-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-096"
    }
  }, [_vm._v("11")])])]), _vm._v(" As we think of individual words, it is important to realize that most words have more than one "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("sense")]), _vm._v(" or meaning. In Philippians 3:15, Paul refers to believers who are ‘mature’ or ‘perfect’ (including himself in this group), using the Greek adjective "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("teleios")]), _vm._v(". This word has more than one sense, with ‘mature’ and ‘perfect’ expressing two of its possible senses (‘initiated’ into a religion is another; BDAG, p. 995). Commentators disagree on which sense Paul is using in Philippians, with some who argue for ‘perfect’ suggesting Paul is being ironic to call out perfectionist tendencies within the Philippian congregation (e.g. "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NET")]), _vm._v(": ‘those of us who are “perfect” . . .’).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("As we recognize that most words have more than one sense, we should be cautious about asserting that any particular word has a "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("basic sense")]), _vm._v(", a meaning that underlies any other senses of the word. Instead, a word might have a primary use in any particular author or corpus (e.g. the Pauline letters). For example, Paul frequently uses the Greek word "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("sarx")]), _vm._v(" (often translated ‘flesh’) to denote the "), _c('span', {
    staticClass: "text"
  }, [_vm._v("realm of human captivity to sin, as contrasted with the realm of the Spirit inaugurated in the Messiah (e.g. Rom. 7:5; 8:3–9). Yet Paul can use ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("sarx ")]), _c('span', {
    staticClass: "text"
  }, [_vm._v("without any negative connotation, as he does in Philippians 1:22 and 24, for his physical ‘body’. Neither usage is more ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("basic")]), _c('span', {
    staticClass: "text"
  }, [_vm._v("; both are possible senses depending on context.")])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_c('span', {
    staticClass: "text"
  }, [_vm._v("The fallacy that any particular word has a basic meaning lends itself to the assumption that it is important to identify the ‘literal’ meaning of a word. Yet if words have multiple senses, not a single basic sense, referring to the ‘literal’ meaning of a word is confusing at best.")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-095-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-095"
    }
  }, [_vm._v("12")])])]), _c('span', {
    staticClass: "text"
  }, [_vm._v(" Paul calls the Philippian believers ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("adelphoi ")]), _c('span', {
    staticClass: "text"
  }, [_vm._v("quite a number of times across the letter (e.g. 1:12; 3:1; 4:1, 8). This term refers to siblings, either male siblings (‘brothers’) or as a generic for siblings of both genders (‘brothers and sisters’).")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-094-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-094"
    }
  }, [_vm._v("13")])])]), _c('span', {
    staticClass: "text"
  }, [_vm._v(" Context determines which reference is in view; and, given Paul’s audience of both female and male believers, he is clearly addressing both when he refers to the Philippians as ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("adelphoi")]), _c('span', {
    staticClass: "text"
  }, [_vm._v(". It is inaccurate to claim that ‘brothers’ is the literal meaning of ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("adelphoi")]), _c('span', {
    staticClass: "text"
  }, [_vm._v(". Instead, in any particular context, ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("adelphoi ")]), _c('span', {
    staticClass: "text"
  }, [_vm._v("can mean ‘brothers’ (e.g. Matt. 4:18; and singular ‘brother’ for ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("adelphos ")]), _c('span', {
    staticClass: "text"
  }, [_vm._v("at Phil. 2:25) or ‘siblings’ (i.e. ‘brothers and sisters’).")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-093-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-093"
    }
  }, [_vm._v("14")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Additionally, words do not mean the sum total of all their possible senses, a fallacy Barr called ‘illegitimate totality transfer’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-092-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-092"
    }
  }, [_vm._v("15")])])]), _vm._v(" For example, Paul uses "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("logizomai ")]), _vm._v("in Philippians 4:8 to call his audience either to ‘think about’ what is virtuous and excellent or to ‘evaluate’ what is virtuous and excellent (BDAG, p. 597). While I argue in the commentary for the latter sense, what is least likely is that Paul means to call forth all possible senses of the word in a single occurrence. He means either one sense or the other; not both (authors play on words only sparingly). Another example is the term "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("apostolos ")]), _vm._v("(Phil. 2:25), which can indicate that Epaphroditus is the Philippians’ ‘messenger’ or their ‘apostle’, with the latter having a semi-technical sense within New Testament usage. In the context of Philippians 2, it seems fairly clear that ‘messenger’ (the non-technical sense) is what Paul intends, so we should not read ‘apostle’ into this occurrence in addition to ‘messenger’.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("A few linguistic terms regularly used in the commentary are defined here.")]), _vm._v(" "), _c('p', {
    staticClass: "bl-t"
  }, [_c('span', {
    staticClass: "Bullet-10pt _idGenCharOverride-2"
  }, [_vm._v("•")]), _c('em', {
    staticClass: "italic"
  }, [_c('span', {
    staticClass: "nl"
  }), _vm._v("Cognate")]), _vm._v(": A word that shares its root with another word; for example, the related noun and verb forms of the same word, such as "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("chara")]), _vm._v(" (‘joy’) and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("chairō")]), _vm._v(" (‘rejoice’).")]), _vm._v(" "), _c('p', {
    staticClass: "bl"
  }, [_c('span', {
    staticClass: "Bullet-10pt _idGenCharOverride-2"
  }, [_vm._v("•")]), _c('em', {
    staticClass: "italic"
  }, [_c('span', {
    staticClass: "nl"
  }), _vm._v("Hapax")]), _vm._v(" (shortened from the Greek, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("hapax legomenon")]), _vm._v("): A word that occurs only once in a particular corpus; in this commentary, used of words that occur only once in the New Testament or once in the Pauline letters.")]), _vm._v(" "), _c('p', {
    staticClass: "bl"
  }, [_c('span', {
    staticClass: "Bullet-10pt _idGenCharOverride-2"
  }, [_vm._v("•")]), _c('em', {
    staticClass: "italic"
  }, [_c('span', {
    staticClass: "nl"
  }), _vm._v("Sense")]), _vm._v(": What is meant or denoted by a word; for example, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kyrios")]), _vm._v(" denotes ‘one who is in a position of authority, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("lord")]), _vm._v(", "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("master")]), _vm._v("’ (BDAG, p. 577).")]), _vm._v(" "), _c('p', {
    staticClass: "bl"
  }, [_c('span', {
    staticClass: "Bullet-10pt _idGenCharOverride-2"
  }, [_vm._v("•")]), _c('em', {
    staticClass: "italic"
  }, [_c('span', {
    staticClass: "nl"
  }), _vm._v("Referent")]), _vm._v(": What a word points to outside its linguistic (literary) context; for example, in Philippians 2:11, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kyrios")]), _vm._v(" is used specifically to refer to Jesus Christ.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("d. Reading Paul’s theology")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Some commentary series, like this one, include discrete attention to the task of contextualizing the messages of the biblical text being studied for a contemporary audience (see Theology sections). This is an appropriate task given that Paul is already theologically and Christologically motivated. As Gupta frames it, Paul’s")]), _vm._v(" "), _c('p', {
    staticClass: "dpy_pros2"
  }, [_vm._v("overarching goal was for his readers/auditors to faithfully, joyfully, and freely embrace the fullness of the gospel . . . [and] those who read Paul’s letters from a perspective and purpose of faith are encouraged to read them "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("theologically")]), _vm._v(" towards better knowledge of God and deeper communion with God."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-091-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-091"
    }
  }, [_vm._v("16")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Given the wide variety of ways theology is understood and accomplished, there is no single, monolithic approach to the contextualizing task. In this commentary, an important starting point is the recognition that Paul is writing pastoral or practical theology to his church audiences. In Philippians, as in Paul’s other letters, we already see Paul ‘theologizing’, and we see him doing so in the light of the specific issues and needs of his audience."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-090-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-090"
    }
  }, [_vm._v("17")])])]), _vm._v(" Given the inherently theological character of Paul’s writings, one way of addressing theology is to focus on Paul’s specific theological reflection in this letter – the theology of Philippians (for examples, see Theology on 2:5–11 and on 4:2–9)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-089-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-089"
    }
  }, [_vm._v("18")])])]), _vm._v(" Another way of conceiving the task is to hear how Paul’s thinking in Philippians fits his theologizing more broadly, across any number of his letters; this is the task of exploring Pauline theology (e.g. Theology on 1:1–11). Another approach, one that has gained significant traction in biblical studies in recent decades, is to attend to the history of interpretation of the text in question."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-088-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-088"
    }
  }, [_vm._v("19")])])]), _vm._v(" This approach highlights ways a text has been understood across the church’s history.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Finally, an approach used in this commentary at a number of points involves constructive theological reflection that engages with the ideas of Philippians from the vantage point of ‘contemporary interests, methods, and concerns’ and with ‘a variety of voices’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-087-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-087"
    }
  }, [_vm._v("20")])])]), _vm._v(" While it is always the case that interpreters bring their own experiences and perspectives to their engagement with the biblical text, the approach of constructive theology makes this vantage point explicit and pursues a conversation with the text with contemporary interests in mind. For example, in my theological reflections on Philippians 2, I engage with the interrelationships of Paul, Timothy, Epaphroditus and the rest of the Philippian believers in conversation with psychological literature on anxiety (see Theology section on 2:19–30). And in the Theology sections on 2:12–18 and 3:1–11, I address how Philippians might be thoughtfully engaged with in relation to Paul’s Jewish identity and Israel’s continuing place as God’s people, in the light of contemporary concerns about anti-Semitism in biblical interpretation (e.g. replacement theologies).")]), _vm._v(" "), _c('h2', {
    staticClass: "ahd",
    attrs: {
      "id": "rch2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/9781789742671_EPUB5#ch7"
    }
  }, [_vm._v("2. Historical matters")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-086-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-086"
    }
  }, [_vm._v("21")])])])]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("a. Author")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Paul is the self-declared author of the letter (1:1); and Philippians is one of seven Pauline letters whose authorship is uncontested by contemporary scholarship (along with Romans, 1 and 2 Corinthians, Galatians, 1 Thessalonians and Philemon)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-085-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-085"
    }
  }, [_vm._v("22")])])]), _vm._v(" While Paul joins his name with Timothy’s (‘Paul and Timothy . . . to . . .’), implying equal authorial standing, it becomes clear from Paul’s first-person address throughout the letter and his references to Timothy in the third person (2:19–23) that Timothy co-sponsors rather than co-writes Philippians. Of all such co-sponsors of Paul’s letters, Timothy is mentioned most often (2 Cor. 1:1; Col. 1:1; 1 Thess. 1:1; 2 Thess. 1:1). There is no indication that Paul relies on an amanuensis for this letter, as he does for Romans (Rom. 16:22) and probably does for other letters that offer a concluding line written in his own hand (1 Cor. 16:21; Col. 4:18; 2 Thess. 3:17; cf. Gal. 6:11).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("If we draw on the concept of the implied reader (see 1b), the Philippian letter illumines a portrait of its author, Paul, as deeply invested in and attached to his siblings in Christ who reside in Philippi. His tone across the letter to this church is one of affection (e.g. 1:3–8; 2:12; 4:1) and concern for their well-being (e.g. 2:28; 4:6–7, 17). Paul offers his own assurances that he is faring well and even rejoicing as he considers his own situation, in spite of being in prison (e.g. 1:12; 2:17–18; 4:18). Any harsh language from Paul is reserved for potential threats from the outside to the well-being of these dear believers in Christ (1:28; 3:2, 18–19).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The portrait of Paul offered by Philippians aligns in some fairly obvious but important ways with what we learn about Paul from other parts of the New Testament (Acts and his other letters). First, when writing Philippians Paul is in prison (1:7, 13–14, 17). This is also the case for three other Pauline letters: Philemon, Ephesians and Colossians. Acts attests to Paul routinely being imprisoned for his preaching of the gospel: in Philippi (Acts 16:19–40), in Jerusalem (21:27 – 23:22), in Caesarea (23:23 – 26:32) and in Rome (28:11–31). Paul also attests to frequent imprisonments in 2 Corinthians 11:23 (cf. Rom. 16:7; 2 Tim. 1:8).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Second, the autobiographical character of Philippians 3:5–6 provides information that contributes to a portrait of Paul, with many of the descriptors in 3:5–6 corroborated elsewhere in the New Testament. Paul’s identification of himself as from ‘the people of Israel’ and ‘of the tribe of Benjamin’ (Phil. 3:5) aligns with his self-identification in Romans: ‘I am an Israelite myself, a descendant of Abraham, from the tribe of Benjamin’ (Rom. 11:1). Paul identifies himself as a Pharisee (Phil. 3:5), which is attested in Acts 23:6 (cf. 5:34); and he refers to his ‘zeal [in] persecuting the church’ (Phil. 3:6). Similarly, Paul, as characterized in Acts, claims, ‘I was just as zealous for God as any of you are today. I persecuted the followers of this Way to their death, arresting both men and women and throwing them into prison’ (Acts 22:3b–4; cf. Gal. 1:13). Paul’s other self-descriptions in Philippians 3 – that he is ‘a Hebrew of Hebrews’ (3:5) and is ‘faultless’ regarding a Torah-based righteousness (3:6) – are paralleled in Galatians 1:14, where Paul claims to previously have been ‘advancing in Judaism beyond many of my own age among my people and . . . extremely zealous for the traditions of my fathers’.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("b. Audience")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The make-up and contours of the Philippian church are most often implicit in Paul’s letter to them. Sketching their portrait involves taking the clues we find in the letter and filling in some amount of detail from what we know about the city of Philippi and its inhabitants in the first century "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" and from what we can glean from Acts 16, which narrates the inception of the Philippian church through the ministry of Paul and his associates.")]), _vm._v(" "), _c('h2', {
    staticClass: "chd"
  }, [_c('em', {
    staticClass: "italic"
  }, [_vm._v("i. The city of Philippi")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The city of Philippi (originally, Krenides) was so named in the fourth century "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("BC")]), _vm._v(" by Philip of Macedon. He subjugated the city shortly after it had been settled by Greeks who had come from the island of Thasos ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _vm._v("360 "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("BC")]), _vm._v("). In 168 "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("BC")]), _vm._v(", the Romans conquered and claimed Philippi, and established a Roman province (Macedonia) from the city and its surrounding region (see Acts 16:12; Phil. 4:15). Mark Anthony resettled the city as a Roman military colony in 42 "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("BC")]), _vm._v(", and granted land to veterans of the Roman army. About a decade later (31 "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("BC")]), _vm._v(") Octavian (Augustus) granted citizenship and land to army veterans, after his victory over Anthony at Actium."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-084-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-084"
    }
  }, [_vm._v("23")])])]), _vm._v(" Some of these veterans had been on the losing side, and Octavian’s gifting of citizenship and land to them was an attempt to neutralize them as a threat to his continuing rule."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-083-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-083"
    }
  }, [_vm._v("24")])])]), _vm._v(" The city of Philippi was a desirable location to obtain and control on a number of counts. It was an agricultural centre, surrounded by fertile farmland which produced grain and wine products. It also sat along a major trade route (the Via Egnatia), running from east to west, and the seaport of Neapolis was only 10 miles to the south-west, offering access to the Aegean Sea and so to Asia Minor (modern-day Turkey; see Acts 16:6–12).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The status of Philippi as a Roman colony had a significant impact on the experience of its residents, which by the time of the mid first century "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" consisted of a population of roughly 10,000."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-082-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-082"
    }
  }, [_vm._v("25")])])]), _vm._v(" As a colony, Philippi ‘carried the status of a city on Italian soil’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-081-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-081"
    }
  }, [_vm._v("26")])])]), _vm._v(" This title ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("ius Italicum")]), _vm._v(") meant that Roman law was utilized in local civic life; in addition, it sometimes provided exemption from taxation."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-080-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-080"
    }
  }, [_vm._v("27")])])]), _vm._v(" Although less than half of the city’s residents would probably have been Roman citizens, those living in Philippi and its environs would have felt Rome’s presence on a regular basis. The minority Roman population of the city and surrounding countryside would have exerted considerable power and influence in the city’s local affairs. Additionally, various Roman institutions, such as the forum, would have assisted in reinforcing Roman power. Philippi, as a Roman colony on Greek soil, seems to be fairly distinct, in that Latin, not Greek, was used by the ruling class. This is evident from inscriptional evidence from first-century Philippi (and note Paul’s use of the Latinized form of ‘Philippians’ at 4:15; see comment there). All of this suggests the significant Roman influence in Philippi when Paul writes his letter. As Oakes suggests, in Philippi ‘Romans owned almost all the land. Romans had all the political control in the city. Romans largely monopolised wealth and high status.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-079-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-079"
    }
  }, [_vm._v("28")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The religious landscape in Philippi at this time included traditional Greek and Roman religious cults such as those of Dionysus, Diana, Apollo and Jupiter; Egyptian and Thracian cults; as well as the Roman imperial cult. In fact, it appears from inscriptional evidence that two of Philippi’s temples were devoted to the imperial cult during the first century."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-078-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-078"
    }
  }, [_vm._v("29")])])]), _vm._v(" The focus of the imperial cult was on worship and veneration of some Caesars along with some of their family members. For example, by the time of the writing of Philippians, former emperors Julius Caesar and Augustus, along with Augustus’s wife Livia and his grandson Claudius, had been deified after their deaths and were worshipped at the imperial cult."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-077-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-077"
    }
  }, [_vm._v("30")])])]), _vm._v(" The influence of the imperial cult was felt across the Roman Empire, and it would have been an institution of some consequence in Philippi."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-076-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-076"
    }
  }, [_vm._v("31")])])]), _vm._v(" Although not an official (legal) requirement, there would have been some amount of social and political pressure to participate in the imperial cult, with this worship practice very likely considered normative."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-075-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-075"
    }
  }, [_vm._v("32")])])])]), _vm._v(" "), _c('h2', {
    staticClass: "chd"
  }, [_c('em', {
    staticClass: "italic"
  }, [_vm._v("ii. The church at Philippi")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The nature of Paul’s relationship with the Philippian believers is evident from both the tone and the content of the letter he writes to them. They appear to have a deep connection with one another, unmarred by antagonism and discord (e.g. 1:7–8; 2:17–18; 4:1). The Philippian congregation had partnered with Paul from the very beginning of their existence as a community (1:5; 4:15), and they chose to contribute financially to Paul’s ministry a number of times (4:10, 16; cf. 2 Cor. 8:1–5). Acts 16 narrates the establishment of the Philippian church ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _vm._v(" "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("AD ")]), _vm._v("50), with Paul, Silas and Timothy arriving in Philippi, where the gospel message both gains traction and also garners opposition (Acts 16:11–40). In the letter, Paul comments obliquely on that early opposition when he refers to the opposition the Philippians currently face for their allegiance to the gospel: ‘you are going through the same struggle you saw I had’ (1:30).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Beginning with the evidence from Philippians itself, we can see quite a number of points of coherence between the letter and the book of Acts that can inform our understanding of the Philippian church. These include the following:")]), _vm._v(" "), _c('p', {
    staticClass: "Numbered-list-t"
  }, [_vm._v("1"), _c('span', {
    staticClass: "nl"
  }), _vm._v("A positive welcome as well as hostile reception of Paul and his message (Phil. 1:28–30; Acts 16:14, 22–24, 30; cf. 1 Thess. 2:2);")]), _vm._v(" "), _c('p', {
    staticClass: "Numbered-list"
  }, [_vm._v("2"), _c('span', {
    staticClass: "nl"
  }), _vm._v("The generosity of the new believers towards Paul (Phil. 4:10–20; Acts 16:15, 34);")]), _vm._v(" "), _c('p', {
    staticClass: "Numbered-list"
  }, [_vm._v("3"), _c('span', {
    staticClass: "nl"
  }), _vm._v("Paul’s imprisonments (Phil. 1:12–13, 30; Acts 16:23–24);"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-074-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-074"
    }
  }, [_vm._v("33")])])])]), _vm._v(" "), _c('p', {
    staticClass: "Numbered-list"
  }, [_vm._v("4"), _c('span', {
    staticClass: "nl"
  }), _vm._v("An emphasis on Roman sensibilities or features (Phil. 1:13, 27; 3:20; 4:22; Acts 16:21, 37);")]), _vm._v(" "), _c('p', {
    staticClass: "Numbered-list-b"
  }, [_vm._v("5"), _c('span', {
    staticClass: "nl"
  }), _vm._v("The prominence of women in the church – Euodia and Syntyche (Phil. 4:2–3) and Lydia and a female slave (Acts 16:13–15, 16–18, 40), suggesting the presence of female leadership within the Philippian house church(es) (see comment on 4:2–3)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-073-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-073"
    }
  }, [_vm._v("34")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In addition to these characteristics, the letter (and Acts) suggests that the Philippian church was primarily Gentile in make-up."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-072-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-072"
    }
  }, [_vm._v("35")])])]), _vm._v(" No synagogue building within the city of Philippi from the first century has been excavated, and Acts does not explicitly mention a synagogue building (cf. Acts 16:13, 16). It is likely that a few people (Jewish and God-fearing; cf. Acts 13:26) met together in Jewish community in spite of having no formal meeting location within the city. In support of this scenario, Acts 16 references Paul and his travelling companions attending on the Sabbath what seems to have been such a gathering at ‘the place of prayer’ (["), _c('em', {
    staticClass: "italic"
  }, [_vm._v("hē")]), _vm._v("]"), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" proseuchē")]), _vm._v("; Acts 16:13, 16), a term often used for Jewish meetings outside Judea."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-071-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-071"
    }
  }, [_vm._v("36")])])]), _vm._v(" If this was a Jewish gathering, then Lydia (originally from Asia Minor) was likely a ‘God-fearer’ attracted to Jewish worship and practices. Evidence from the names attributed to church members at Philippians 2:25 and 4:2–3 (along with Lydia’s name provided in Acts) may indicate that these were Greek or Roman members of the community, although some Jews did have Greek or Roman names in addition to their given (Jewish) name. Paul’s minimal use of the Old Testament in the letter may also indicate a primarily non-Jewish audience.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("If we consider the potential size of the church at Philippi, Paul’s quite specific greeting to ‘the overseers and deacons’ which he adds to his general greeting for all believers (1:1) could suggest a fairly substantial group; for example, over fifty people."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-070-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-070"
    }
  }, [_vm._v("37")])])]), _vm._v(" In the Graeco-Roman world, the interiors of larger homes (with their courtyards providing additional space) could accommodate up to forty to fifty people."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-069-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-069"
    }
  }, [_vm._v("38")])])]), _vm._v(" This suggests that there may have been more than one house church in the city that made up ‘all God’s holy people in Christ Jesus at Philippi’ (1:1). In the account of the founding of the Philippian church (Acts 16), we hear that Lydia (16:14–15, 40) invites Paul and his companions into her home (with her entire household or "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("oikos")]), _vm._v(" being baptized). According to Gehring, textual details indicate that Lydia probably owned her own "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("domus")]), _vm._v(", a more spacious city house."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-068-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-068"
    }
  }, [_vm._v("39")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Finally, the ethnic and economic make-up of the Philippian house church(es) would have been quite diverse. While it has sometimes been assumed that Philippi and so the Philippian church consisted primarily of veterans of the Roman army (see 2b[i]), Oakes’s modelling analysis suggests that only about 40% of the residents of Philippi were Roman citizens (which compares with 14% elsewhere in the Roman Empire)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-067-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-067"
    }
  }, [_vm._v("40")])])]), _vm._v(" As we consider the specific make-up of the Philippian house church(es), Oakes has also proposed that the Philippian church would have been primarily Greek, not Roman, and would have included a broad social spectrum. Oakes’s modelling for the demographics of first-century Philippi, and specifically the church there, suggests that only about one-third of the church was likely to have been Roman, with the majority being Greek (and including some non-Greek slaves)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-066-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-066"
    }
  }, [_vm._v("41")])])]), _vm._v(" He further proposes significant social diversity in the church reflecting the social diversity of Philippi, including colonist farmers, slaves, those living in poverty, people involved in service (e.g. trades), and (potentially) a small percentage of elite Romans."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-065-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-065"
    }
  }, [_vm._v("42")])])]), _vm._v(" Envisioning such diversity in the church clarifies various features of the letter, including its themes of unity and suffering, and the exhortation to press against preoccupation with status.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("c. Provenance")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The primary suggestions for the letter’s provenance (Paul’s location as he writes Philippians) are Rome, Caesarea and Ephesus. Paul makes it clear in the letter that he is in prison as he writes (1:7, 12–14, 17), although the location of his imprisonment is left unspecified. An imprisoned Paul aligns Philippians with Paul’s other so-named ‘prison epistles’: Philemon, Ephesians and Colossians, though none of these provides clear indication of provenance either. Acts narrates the imprisoning of Paul in four locations: Philippi (Acts 16:19–40), Jerusalem (21:27 – 23:22), Caesarea (23:23 – 26:32) and Rome (28:11–31). Paul himself attests to being in prison quite ‘frequently’ (2 Cor. 11:23), so it is very possible he was imprisoned in more locations than Acts designates.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In Acts, Paul’s lengthier imprisonments occur in Caesarea and Rome (usually dated "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" 58–60 and "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" 60–63, respectively). Both of these have been suggested as the provenance for Paul’s prison letters and so for Philippians. Roman provenance has been traditionally assumed, especially given Paul’s references to the ‘palace guard’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praetorium")]), _vm._v("; Phil. 1:13) and to ‘Caesar’s household’ (4:22). Bockmuehl suggests that Paul’s rhetorical purpose for highlighting these two Roman institutions is ‘precisely that at a time of persecution for both him and the Philippians, the gospel is none the less advancing even into the very centres of Roman power’, an argument that has the most weight if Paul were writing from Rome."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-064-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-064"
    }
  }, [_vm._v("43")])])]), _vm._v(" The primary problem raised for Roman provenance is the extensive distance for travel between Philippi and Rome ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _vm._v("800 miles or 1,300 km) and the number of trips between Paul and the Philippians implied in the letter and especially in 2:25–30 (see below).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Scholars have raised the possibility of Ephesus as a location for Paul’s imprisonment while writing Philippians, and this view has gained traction in recent years."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-063-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-063"
    }
  }, [_vm._v("44")])])]), _vm._v(" An Ephesian provenance would lessen significantly the distance to travel between Paul and Philippi ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _vm._v("300 miles or 500 km), and those who hold that Paul wrote Philippians from Ephesus note that the term "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praetorium")]), _vm._v(" could be used to refer to a provincial governor’s headquarters (with its attending guard; cf. Acts 23:35), as well as to the imperial guard itself stationed in Rome. These proponents also indicate that the language of ‘Caesar’s household’ easily refers to the civil servants of the empire (of Caesar) and not only to the immediate family of the emperor. Proponents of Ephesian provenance locate that imprisonment within Paul’s general reference to his frequent imprisonments (2 Cor. 11:23; also cf. 1 Cor. 15:32). Against the view that Paul was writing from Ephesus is the lack of evidence of a "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praetorium ")]), _vm._v("(a governor’s headquarters) in Ephesus and no direct evidence for Pauline imprisonment there.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("We can briefly mention Caesarean provenance, which has also been argued, although it has few proponents among contemporary scholars. In favour of this proposal is the presence of a "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praetorium")]), _vm._v(" in Caesarea; as Acts 23:35 notes, Paul was kept ‘under guard in Herod’s palace’ (using "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praitōrion")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In this commentary, Roman provenance is assumed. I consider that the arguments for it are stronger than for either Ephesus or Caesarea. And the arguments against it are not as significant as often suggested. Those who argue against Roman provenance raise two main difficulties. One issue involves Paul’s future plans, as expressed in Romans 15:23–24. We read there that Paul intends to travel westwards to Spain rather than return eastwards to areas where he had formerly ministered. This plan expressed to the Roman church ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" 56–58) appears to contradict his stated intention to return to the Philippian church (Phil. 1:26; 2:24), if Philippians was written from Rome and so after "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("AD")]), _vm._v(" 60 (see 2d). An earlier composition date for Philippians (as in the Ephesus scenario) would alleviate this purported problem. Yet there is no need to hold Paul to his intentions expressed in Romans for a westwards mission rather than a return to the east. After writing of this plan and as he sat in prison, Paul could easily have felt a need to return to some of his churches in the east to encourage them (Phil. 1:25–26) before engaging in a mission to Spain."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-062-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-062"
    }
  }, [_vm._v("45")])])]), _vm._v(" Paul’s future plans as expressed in his letters are, after all, human, contingent plans (cf. Rom. 1:9–13; Jas 4:13–17).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The second and more pressing difficulty raised for Roman provenance has to do with the distance between Rome and Philippi, which involved about 800 miles ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("c.")]), _vm._v("1,300 km) of travel, requiring at least a month. This significant distance raises problems when coupled with the number of trips back and forth between the Philippians and Paul mentioned in the letter itself, often tallied as five or more trips (Phil. 2:25–30; 4:18). A careful reading of 2:25–30, however, suggests fewer trips than often assumed. Philippians 2:25–27 (cf. 4:18) does not require more than four trips between the Philippians and Paul from beginning to end.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("After the Philippians heard news of Paul being in prison (trip 1) and sent Epaphroditus to him (4:18; trip 2), and before Paul sent Epaphroditus back to the church (2:25; trip 3), there is only one necessary trip between Paul’s location and Philippi. This single trip would have involved someone bringing news of Epaphroditus’s illness to the Philippian church (trip 4). No additional dispatch from the Philippians to Paul is required, since Paul does not mention hearing of their response to this news. Instead, he writes of the distress that Epaphroditus feels ‘because you heard he was ill’ (2:26), a sensibility on the part of Epaphroditus that could be readily assumed if someone had been dispatched to Philippi with this news. Fee raises the additional possibility that Epaphroditus’s illness was contracted during his journey to Rome so that the news of it did not require the entire distance to be traversed. If a travelling companion returned to Philippi with the news (a shortened trip 4), Epaphroditus could have pressed on in spite of his illness to reach Paul, potentially accounting for his close brush with death (2:27, 30)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-061-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-061"
    }
  }, [_vm._v("46")])])]), _vm._v(" We should also note that Paul’s imprisonment in Rome was not of short duration; Acts mentions two years and the narrative concludes before Paul is released (Acts 28:30).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("If Paul is imprisoned in Rome when he writes to the Philippians, and given his description of his engagement with the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("praetorium ")]), _vm._v("or ‘palace guard’ (Phil. 1:13), he is in military custody (the Roman "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("custodia militaris")]), _vm._v("), rather than the more severe "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("carcer ")]), _vm._v("(e.g. Acts 16:22–24) or lenient "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("custodia libera")]), _vm._v(", which was a kind of house arrest under non-military supervision (e.g. a family member)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-060-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-060"
    }
  }, [_vm._v("47")])])]), _vm._v(" The last-mentioned was reserved for those of elite status. Military custody would involve being chained, usually by the wrist, to one or two soldiers with heavy iron "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("manacles")]), _vm._v(","), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-059-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-059"
    }
  }, [_vm._v("48")])])]), _vm._v(" and so Paul’s references to being ‘in chains’ (1:13, 14, 17) are not metaphorical but a physical description of his experience being continually shackled to his guard(s). In this situation, the presence of friends or family would be crucial for physical and mental preservation. Paul’s references to the important roles that Epaphroditus and Timothy have played in his welfare (2:19–30) fit within these difficult realities of imperial imprisonment.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("d. Date")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The date for the composition of Paul’s letter to the Philippians correlates with the location from which he writes. The date ranges for each of the three primary possibilities for Paul’s imprisonment are as follows:")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-t"
  }, [_vm._v("Ephesus: Early to mid 50s")]), _vm._v(" "), _c('p', {
    staticClass: "chi1"
  }, [_vm._v("Caesarea: 58–60")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-b"
  }, [_vm._v("Rome: Early 60s")]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Given the arguments above that the imprisoned Paul writes from Rome (see 2c), a date of "), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("AD")]), _vm._v(" 60–63 is most likely.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("e. Occasion and purposes")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("In spite of being a relatively brief letter, the text of Philippians suggests a number of reasons for the letter’s composition. A central ‘occasion’ of the letter (the situation that has initially ‘occasioned’ it) is the return to Philippi of Epaphroditus, a member of the Philippian congregation. Paul writes, ‘I think it is necessary to send back to you Epaphroditus’ (2:25). The church had earlier sent Epaphroditus as their ‘messenger’ to attend to Paul’s needs while he was in prison (2:25). He had brought along financial gifts from the church to support Paul (4:18), and he probably was meaning to stay to provide personal presence and encouragement to Paul as a liaison for the Philippians (see comment on 2:25). But Epaphroditus had become gravely ill, whether on his way to Paul or after his arrival (2:26–27; see 2c). Once he had recovered, Paul moved quickly to return him to the Philippian believers so they would not be worried but would experience joy at the restoration and return of their brother in the faith (2:28–29).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Epaphroditus’s return to Philippi would have provided the impetus for Paul to write to the church there. In the first-century world, letters were a primary form of communication for people separated by many miles. If someone were setting off on a journey, it would be routine, even expected, for that person to carry a letter or letters from family or friends at the point of departure to those they wanted to communicate with at the point of destination. In this context, travelling became an opportunity for letter exchange."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-058-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-058"
    }
  }, [_vm._v("49")])])]), _vm._v(" The return of Epaphroditus provided Paul with an opportunity to communicate with his beloved friends at Philippi on any number of other matters that were on his mind and were for their benefit. These include an expression of gratitude for their gift, assurances about Paul’s own situation in prison, encouragement towards unity in spite of opposition, and a number of warnings about certain threats to the congregation’s growth in faith.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("One purpose of the letter is the opportunity it afforded for Paul to acknowledge the gifts the Philippians had sent through Epaphroditus (4:10–20). In line with conventions in the Graeco-Roman world regarding giving and receiving gifts, Paul does not provide an explicit ‘thanks’. Instead, he carefully crafts an implicit expression of gratitude (4:10, 14), while providing a Christological (4:13) and theological (4:18–19) framework for understanding the nature of Christian partnership and reciprocity (see comments on 4:10–20). Although coming at the end of the letter, Paul’s gratitude for the gift the Philippians had sent through Epaphroditus (4:18) is an important reason for the letter, and his appreciation is already anticipated in his opening thanksgiving (1:3–5, 7).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Another purpose of the letter is to assure the Philippians that they do not need to be concerned about Paul as he sits in prison. He is keen to let his Philippian friends know that, rather than being troubled by his circumstances, he is encouraged by how his imprisonment, somewhat surprisingly, is advancing the gospel about Jesus even further (1:12–18). And he wants to comfort them with this news."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-057-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-057"
    }
  }, [_vm._v("50")])])]), _vm._v(" He also assures the Philippian believers that he is convinced his forthcoming trial will result in his vindication, not his execution (1:19–26; 2:24), so that Paul will be able to return to Philippi and be with the believers there again (1:25–26).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Another primary purpose of the letter is to encourage the Philippian congregation towards unity, even as they are facing opposition for their allegiance to Christ. The first exhortation of the letter points in this direction, with its dual focus on living with an undivided loyalty to the gospel of Christ and doing so with a unified stance towards outside opposition (1:27–28). The repetition of Paul’s admonitions for unity (1:27; 2:2; 4:2), along with a shared mindset based on Christ’s mindset (2:2, 5), suggest that the Philippian house church(es) needed, in particular, to hear and actively respond to this call to unity. At 4:2–3, Paul will address a particular issue between two of his co-workers in Philippi, Euodia and Syntyche, who need the reminder to ‘be of the same mind in the Lord’ (4:2).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Finally, Paul writes to warn his siblings in Christ about certain threats that could impede their growth in faith and faithfulness. These warnings come in a few, fairly oblique references to what have been called the Philippian ‘opponents’ (‘those who oppose you’, 1:28). Given the brevity and rhetorical shaping of these descriptions, much ink has been spilled in an attempt to identify (1) who these opponents are and (2) whether the multiple hints about them (1:28; 3:2; and 3:18–19) point to a single group or to multiple ‘opponents’.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("f. The Philippian ‘opponents’")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Determining the identity and number of the Philippian ‘opponents’ is complicated by the brevity of Paul’s descriptions of them (1:28; 3:2; 3:18–19; cf. 1:15–17), which requires some amount of ‘reading between the lines’ for any reconstruction of the situation and interpretation of Paul’s meaning. It is rather ironic that the dearth of specifics Paul provides about these ‘opponents’ accounts for the breadth of discussion about them. In spite of the extended scholarly discussion about these opponents, we should keep in mind that the ‘threat of opponents is not Paul’s primary reason for writing’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-056-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-056"
    }
  }, [_vm._v("51")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The first mention of any kind of opposition is Paul’s reference to believers in his own context who are attempting to rival Paul in their preaching of the gospel (1:15–17). These believers, however, are not in Philippi but are in the vicinity of Paul’s imprisonment (probably Rome; see 2c). So this group is not a part of any opposition the Philippian believers are experiencing."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-055-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-055"
    }
  }, [_vm._v("52")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul’s initial exhortation to the Philippian church includes a brief reference to ‘those who oppose you’ (1:28). Paul encourages the church to be unified in their complete allegiance to the gospel of Christ (1:27). As they take on this stance, they will have no need to fear these opponents. While the source of the opposition is not mentioned, it seems likely that it comes from unbelievers in Philippi given Paul’s reference to their future destruction if they persist (1:28). Given the confluence of motifs in 1:27–28, the Philippian believers are probably experiencing social ostracization arising from their singular allegiance to Jesus, a common situation for the early church (cf. 1 Pet. 3:15–16). Such undivided loyalty would involve a rejection of all other deities, including participation in the imperial cult, eliminating their temple involvement and all the benefits – religious, social and economic – derived from such associations (see comment on 1:28)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-054-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-054"
    }
  }, [_vm._v("53")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("At two points in Philippians 3, Paul mentions people whose negative influence poses a threat of some kind to the Philippian church (3:2 and 3:18–19)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-053-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-053"
    }
  }, [_vm._v("54")])])]), _vm._v(" Some scholars join these two references to the one in 1:28 and consider all three to be describing a single group of opponents."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-052-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-052"
    }
  }, [_vm._v("55")])])]), _vm._v(" It is more likely, however, that Paul is describing outsiders (unbelievers) in 1:28 and a ‘Judaizing’ element or influence from within the early church at 3:2. Paul’s reproof describing the latter as ‘those dogs, those evildoers, those mutilators of the flesh’ (3:2) points in this direction. If so, Paul refers to (the views of) some Jewish Christians, whose goal was to persuade Gentile believers to convert to Judaism and so participate fully in the new messianic community. As I will suggest in the commentary, Paul seems to be referring more to the negative influence of ‘Judaizing’ ideas than to the actual presence or impending arrival of Judaizers in Philippi (see comment on 3:2). Paul is warning his Gentile audience against this potential threat and provides a counter-example in his own focus on boasting in and knowing Christ alone (3:4–11).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Finally, Paul refers to people who are ‘enemies of the cross of Christ’, whose ‘destiny is destruction . . . god is their stomach, and . . . glory is in their shame’, with ‘their mind . . . set on earthly things’ (3:18b–19). More than a few scholars identify this group with the one Paul warns against in 3:2."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-051-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-051"
    }
  }, [_vm._v("56")])])]), _vm._v(" It proves difficult to reconstruct a clear profile of this group due to Paul’s intense rhetoric and general descriptors. Yet it does seem that the moniker ‘enemies of the cross’, along with the description of a mindset focused only on the present, points to believers who have capitulated to external pressure to avoid any suffering or persecution (and so can be called ‘enemies of the cross’). If so, Paul may be speaking rather generally about any believers, at Philippi and elsewhere, who are being led in a dangerous direction by their desire to avoid suffering for the cause of Christ (see comments on 3:18–19). The general warning could, then, include any Gentiles in Philippi who might be tempted to become proselytes in Judaism to avoid societal pressures (see comment on 3:2). Whatever their specific identity (or identities), these people are divided in their loyalties and no longer have the eschatological mindset Paul exemplifies and commends (3:7–21).")]), _vm._v(" "), _c('h2', {
    staticClass: "ahd",
    attrs: {
      "id": "rch3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/9781789742671_EPUB5#ch8"
    }
  }, [_c('strong', {
    staticClass: "bold"
  }, [_vm._v("3. Literary matters")])])]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("a. Genre")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Ancient letters ranged from formal epistles, which virtually functioned as treatises and were intended for a wide audience, to personal letters, often quite brief and covering an array of everyday, often mundane subjects. Philippians fits somewhere in the middle of that spectrum. It has a strongly relational tone and is fairly informal at a number of points (e.g. 1:12–18; 2:19–30), suggesting its personal nature. Additionally, its audience is quite specific – the church community in Philippi. Yet its length is not insubstantial; and it addresses a number of significant theological and ethical themes, carefully woven across the letter. And as we can see from other Pauline letters, he does consider his audience-specific letters appropriate for the edification of other church communities (Col. 4:16).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("As an example of a first-century letter, Philippians demonstrates a basic epistolary structure and elements: identification of writer(s) and recipient (1:1); greeting (1:2); prayer and/or thanksgiving (1:3–11); body (1:12 – 4:20); and personal news, greetings and well-wishes (4:21–23). We can also note some amount of adaptation of these conventional elements in the letter. For example, the prayer/thanksgiving section is quite well developed (a regular Pauline practice) as compared with typical ancient letters (1:3–11). The concluding greetings and well-wishes, on the other hand, are quite brief (4:21–23) in comparison with other Pauline letters. The body of an ancient letter would address whatever topics and concerns the author wanted to communicate to the recipient. In Philippians, Paul addresses a number of discrete topics, but with a sense of thoughtful arrangement (e.g. alternating exhortations with exemplars) and with later parts of the letter recapitulating earlier ideas and themes (e.g. 4:2–20).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In the first-century world, there was quite a variety of types of letters. Determining if Philippians resembles a specific kind of letter has produced no consensus to date. The most common suggestion is that Philippians is a letter of friendship, whose typical features included (1) reference to the ‘absence’ the two friends or parties were experiencing (see Phil. 1:27; 2:12); (2) expression of affection, even longing (e.g. 1:7–8; 2:26); (3) a focus on unity and reciprocity (e.g. 1:27 – 2:4; 4:2–3, 10–20); and (4) recognition of enmity that provides a backdrop to friendship (e.g. 1:28; 3:2, 18–19)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-050-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-050"
    }
  }, [_vm._v("57")])])]), _vm._v(" A common difficulty raised for identifying Philippians as a letter of friendship is its lack of any specific language for friendship, such as "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("philia ")]), _vm._v("(‘friendship’) or "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("philos ")]), _vm._v("(‘friend’)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-049-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-049"
    }
  }, [_vm._v("58")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Alexander has argued that Philippians is a family letter, a type of letter centred on exchanging news between parties and focused on a concern for the welfare of the recipient. Alexander suggests that Paul has ‘adapted and expanded’ this genre form to bolster the familial connections between himself and the church at Philippi."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-048-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-048"
    }
  }, [_vm._v("59")])])]), _vm._v(" Another suggestion comes from Holloway, who proposes that Philippians resembles Graeco-Roman ‘consolations’, and specifically the consolatory letter. In this letter type, an author aims to lessen the grief of the recipient and does so through rational argument."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-047-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-047"
    }
  }, [_vm._v("60")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The lack of scholarly consensus regarding whether the internal evidence of Philippians supports its inclusion within any particular epistolary type might suggest that Philippians offers a mixed form or that it simply does not fit cleanly into one of these or other categories. As Klauck concludes, ‘among the letter types, none of which is ideally realized in Philippians, one can consider next to the friendly letter also the family letter, and perhaps even the administrative letter’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-046-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-046"
    }
  }, [_vm._v("61")])])]), _vm._v(" It is important to note that the various scholars who argue that Philippians is a particular type of ancient letter bring their understanding of that genre to bear interpretively on the letter’s structure and substance."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-045-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-045"
    }
  }, [_vm._v("62")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("There are at least two embedded genres within the letter that have potential ramifications for interpretation: a virtue list and poetry (for the virtue list of 4:8, see comment there). The most significant hermeneutically is the poetic refrain in Philippians 2:6–11. Some scholars argue that this section, traditionally identified as a Christological hymn, falls short of identification as poetry and is instead an example of elevated prose."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-044-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-044"
    }
  }, [_vm._v("63")])])]), _vm._v(" While its identification as a pre-existing Christian hymn is unconvincing, a number of its features suggest that at least 2:6–8, if not the entire section, should be identified and interpreted as poetry."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-043-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-043"
    }
  }, [_vm._v("64")])])]), _vm._v(" First and foremost, the careful balancing of lines is apparent and fits the genre of Jewish poetry. The first line pair is balanced by virtue of its contrasting ideas (i.e. antithetical parallelism):"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-042-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-042"
    }
  }, [_vm._v("65")])])])]), _vm._v(" "), _c('p', {
    staticClass: "chi1-t"
  }, [_vm._v("6a Who, though being in the form of God,")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-b"
  }, [_vm._v("6b did not consider equality with God as something to be exploited")]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Two lines (7c and 8a) are clearly synonymous:")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-t"
  }, [_vm._v("7c Being made in the likeness of human beings")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-b"
  }, [_vm._v("8a and being found in appearance as a human being")]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("And two sets of lines are balanced by the use of a participle (of means) to extend and clarify an initial action of Christ:")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-t"
  }, [_vm._v("7a rather, he emptied himself")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-b"
  }, [_vm._v("7b by taking the form of a slave")]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("and,")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-t"
  }, [_vm._v("8b he humbled himself")]), _vm._v(" "), _c('p', {
    staticClass: "chi1-b"
  }, [_vm._v("8c by becoming obedient to death")]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Additionally, these two line sets, though not adjacent, share a close structural correspondence to each other: a (past tense) verb + ‘himself’ + a participle clause.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Another feature of poetry present across 2:6–11 is a decided preference for synonyms to express key ideas rather than a repetition of terms, which might account for the number of unusual (for Paul) terms employed in 2:6–8."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-041-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-041"
    }
  }, [_vm._v("66")])])]), _vm._v(" For example, to indicate the idea of Christ’s ‘form’ or ‘likeness’, Paul uses three distinct, though closely related, terms: "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("morphē ")]), _vm._v("(‘nature’ or ‘form’), "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("homoiōma ")]), _vm._v("(‘likeness’) and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("schēma ")]), _vm._v("(‘appearance’). Given the tendency in poetry towards a use of synonyms, the differences in nuance among these terms should not be unduly pressed. When repetition is used in poetry, however, it often functions as an indicator of structure; for example, providing clues for delineating stanzas. The twofold use of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("morphē ")]), _vm._v("(‘nature’ or ‘form’) provides a structural clue for the poem, marking a key contrast: Christ is in the ‘form’ of God (2:6a) and he takes the ‘form’ of a slave (2:7b). This repetition marks the first and last lines of what is probably the first stanza (2:6a–7b)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-040-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-040"
    }
  }, [_vm._v("67")])])]), _vm._v(" The narrative cast to this Christ poem has antecedents in the Old Testament and other Jewish literature (e.g. Pss 77; 105; 106; Wis. 10).")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("b. Reading Paul’s rhetoric")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("‘Paul’s rhetoric’ refers to his persuasive design in Philippians or any of his letters. While within the classical tradition rhetoric referred to persuasion developed in speech or oratory, its patterns and practices could also apply to written works."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-039-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-039"
    }
  }, [_vm._v("68")])])]), _vm._v(" An issue that contemporary Bible readers might raise for understanding the impact of ancient rhetoric on Paul’s letters is the tendency in modern discussions to associate ‘rhetoric’ with deception. When we say someone is being highly ‘rhetorical’, we usually mean that he or she is emphasizing form over substance, sometimes to the point of being manipulative."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-038-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-038"
    }
  }, [_vm._v("69")])])]), _vm._v(" Yet the study of rhetoric was the culminating stage of a Graeco-Roman education and, even though there were differing opinions about rhetoric within the philosophical tradition (e.g. some critique of form over substance), it would have been understood more broadly and positively than the term often communicates today.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Much of the focus of rhetorical criticism in New Testament studies has been on structural and stylistic matters, with the suggestion that formal rhetorical practices were used in at least some of Paul’s letters. Nevertheless, the application of rhetorical analysis for understanding the structure of Paul’s epistles, including Philippians, has yielded little consensus. Most of the letter defies easy classification when using rhetorical categories related to a letter’s organization (e.g. "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("exordium")]), _vm._v(","), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" narratio")]), _vm._v(","), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" confirmatio")]), _vm._v(","), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" peroratio")]), _vm._v(").")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The use of rhetoric, however, should not be confined to structural and stylistic analysis, what deSilva refers to as a displaced emphasis on ‘form over function’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-037-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-037"
    }
  }, [_vm._v("70")])])]), _vm._v(" In support of a broader application, we could note that Aristotle’s "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Rhetoric")]), _vm._v(" addresses formal issues only in his final, shorter book. The first two longer books focus on the creation and development of arguments."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-036-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-036"
    }
  }, [_vm._v("71")])])]), _vm._v(" It is in this area that rhetorical analysis offers a fruitful companion to exegetical work.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Three primary categories within rhetoric highlight how speakers or writers conceive of and develop their persuasive design: "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("ethos")]), _vm._v(", "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("pathos")]), _vm._v(" and"), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" logos")]), _vm._v(". Even speakers and hearers who were not formally trained in rhetoric would have understood and drawn upon these basic constructs. In fact, Kennedy suggests that these categories are something of a universal phenomenon."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-035-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-035"
    }
  }, [_vm._v("72")])])]), _vm._v(" "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Ethos")]), _vm._v(" refers to the speaker’s character and reputation, while "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("pathos")]), _vm._v(" involves ‘the emotions and tendencies that can be expected of an audience’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-034-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-034"
    }
  }, [_vm._v("73")])])]), _vm._v(" "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Logos")]), _vm._v(" consists in the internal logic and content of the speech, with this category often being the primary focus of exegetical work in New Testament letters. Nevertheless, by including attention to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("ethos ")]), _vm._v("and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("pathos ")]), _vm._v("in the textual analysis of a letter, the interpreter can tap into tone as well as semantics – the emotive sense of a text as well its cognitive sense. In his attention to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("logos")]), _vm._v(", "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("pathos")]), _vm._v(" and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("ethos")]), _vm._v(", deSilva describes the purpose of this kind of analysis as")]), _vm._v(" "), _c('p', {
    staticClass: "dpy_pros2"
  }, [_vm._v("identifying and entering into the inner argumentation of the text, discerning varying kinds of appeal that make the text an address to the ‘whole person’ of the hearer (i.e. to his or her mind, feelings, and connection with the speaker)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-033-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-033"
    }
  }, [_vm._v("74")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In this commentary, I offer a concluding paragraph on ‘Paul’s rhetorical emphases’ under Context for each section of the letter. I do so by using the three categories of ethos, pathos and logos (without italics, since these terms have moved from Greek into English use). I do so because I have found it helpful to consider how Paul draws on his own situation and his existing relationship with the Philippian believers to persuade them towards particular dispositions and actions. For instance, he has enough relational capital with the Philippians to ask them expectantly to ‘make my joy complete by being like-minded’ (2:2). It also proves helpful to consider how Paul might be expressing himself to evoke and/or address the emotions of his audience. Paul’s extended discussion of the illness and return of Epaphroditus to Philippi (2:25–30) relies on Paul’s assumption that the rest of the Philippian church was concerned for the well-being of this fellow believer (2:26, 28). Paul returns Epaphroditus to Philippi so that the Philippians ‘may be glad’ (2:28). Holloway goes so far as to suggest that Paul’s main objective in the letter was their consolation."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-032-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-032"
    }
  }, [_vm._v("75")])])]), _vm._v(" Interpretively, insights can be gained from analysing how logos, ethos and pathos work together within Paul’s rhetorical design.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("c. Integrity of the letter")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The ‘integrity’ of a letter refers to its unity – its coherence as a single letter written on a single occasion. In the recent history of interpretation, the integrity of Philippians has been an open question. Those who think that the canonical Philippians contains two or more Pauline letters base that conclusion on two textual anomalies: the seemingly abrupt change of tone and direction at 3:1a and 3:1b–2, as well as the unexpected placement of Paul’s expression of gratitude for the Philippians’ gift at the end of the letter (4:10–20). As a result, some have suggested that 3:2–21 (or up to 4:3) and 4:10–20 are distinct letters, written at different times. Reumann, for example, proposes that Paul wrote a first letter to Philippi, comprising 4:10–20, in the early to mid 50s. A second letter, consisting of much of the canonical Philippians, was written and sent in the mid 50s. Finally, the polemically driven 3:2–21 was written shortly thereafter to address Jewish-Christian missionaries pressing for the circumcision (i.e. the conversion) of Gentile Christ-followers."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-031-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-031"
    }
  }, [_vm._v("76")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Yet the textual features that have given rise to theories of multiple letters can be adequately addressed by the oral and aural characteristics of Philippians. Specifically, the letter was intended to be read out to the Philippian church – a routine practice in that context – potentially by its carrier, Epaphroditus, or one of the church’s leaders. This aural cast of the letter helps to explain what seems to be an abrupt transition in 3:1, which begins with "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("to loipon")]), _vm._v(", often used to indicate ‘finally’. Achtemeier has proposed that in the aural reading of Philippians, such a decisive conjunction would helpfully signal a significant change of topic (as occurs in 3:1–21)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-030-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-030"
    }
  }, [_vm._v("77")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Issues of orality could also account for Paul’s decision to wait until its conclusion to address the gift he has received from the Philippians. In a context where the letter was read out, Paul’s carefully worded thanks would have left a strong impression on the Philippians (intentionally so, it seems), especially coupled with the very brief concluding greetings."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-029-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-029"
    }
  }, [_vm._v("78")])])]), _vm._v(" Additionally, there are a significant number of literary connections between 1:3–11 and 4:10–20, suggesting that these opening and concluding sections together serve as ‘a well-crafted frame’, and supporting the letter’s unity."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-028-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-028"
    }
  }, [_vm._v("79")])])]), _vm._v(" All in all, a close reading of the text as it stands (the canonical Philippians), with historical realities in view, supports the letter’s integrity.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("d. Structure")])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Each of Paul’s letters has a distinctive structure and pattern of thought. Philippians has a quite personal cast to it, beginning and ending as it does with Paul’s reflections on his own situation in prison (1:12–26) and his grateful response to the gift he has received from the Philippian congregation (4:10–20)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-027-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-027"
    }
  }, [_vm._v("80")])])]), _vm._v(" In much of the rest of the body of the letter, Paul follows a flexible pattern of alternating exhortations and exemplars. He begins with a series of exhortations in 1:27 – 2:4, followed by the paradigm of Christ himself – the pinnacle of exemplars (2:5–11). The following sketch visualizes this interchange across the letter (for a full outline, see Analysis):")]), _vm._v(" "), _c('p', {
    staticClass: "linespace"
  }), _vm._v(" "), _c('table', [_c('tbody', [_c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exhortations")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("1:27 – 2:4")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exemplar: Jesus")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("2:5–11")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exhortations")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("2:12–18")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exemplar: Timothy")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("2:19–24")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exemplar: Epaphroditus")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("2:25–30")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exemplar: Paul")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("3:1 – 4:1")])])]), _vm._v(" "), _c('tr', [_c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("Exhortations (summative)")])]), _vm._v(" "), _c('td', {
    staticClass: "none1"
  }, [_c('p', {
    staticClass: "abb_fo"
  }, [_vm._v("4:2–9")])])])])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("It should be noted that the exemplar segments typically include some direct exhortation, as in 2:5, 29; 3:2, 16 (cf. 4:1).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Attention to exemplars for the formation of an audience is a common rhetorical strategy in the Graeco-Roman context (expressed by the Latin "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("exemplum ")]), _vm._v("and the Greek "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("paradeigma")]), _vm._v("). According to Smit, Paul uses exemplars not only for moral purposes but for the identity formation of the Philippian congregation. In Philippians, Paul desires ‘to steer an entire community in a particular direction’ and he does so by means of exemplars."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-026-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-026"
    }
  }, [_vm._v("81")])])])]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("e. Use of the Old Testament")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("There is a fairly modest use of the Old Testament in Paul’s letter to the Philippians. This is especially apparent when comparing the significant number of citations and allusions to the Old Testament in Galatians, 1 and 2 Corinthians and Romans. In these letters, Paul regularly draws on the Scriptures, often with his characteristic introductory formula ‘it is written’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("gegraptai")]), _vm._v("; used around thirty times in these four letters). Although there are no such explicit citations in Philippians, Paul does allude to a number of Old Testament texts or images, and he does so by using the Septuagint (the Greek Old Testament, also referred to as the ‘"), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("LXX")]), _vm._v("’). He has multiple purposes for drawing on the Scriptures, including to inform his Christology and to shape the Philippians to live more fully their identity as the people of God.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("One of the clearest allusions, and one that contributes significantly to Christology, comes from Isaiah 45:23 (‘every knee should bow . . . and every tongue acknowledge’) at the conclusion of the ‘Christ poem’ (Phil. 2:6–11, specifically 2:10–11). Paul draws on this Isaianic text – with a clear contextual message in Isaiah 45 that Israel’s God is the only true God – to affirm the exaltation of Jesus the Messiah and his inclusion in the divine identity and sovereignty and so the appropriateness of ascribing worship to him."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-025-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-025"
    }
  }, [_vm._v("82")])])]), _vm._v(" This allusion provides an example of a Christological use of the Old Testament (for another example, see Gen. 1:26 in Phil. 2:7).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("A number of allusions occur in Philippians 2:12–18, with Paul drawing on this cluster of Old Testament texts and images to shape the Philippians’ communal identity. In this section focused on exhortation, Paul brings together more than one allusion or echo from texts focused on Israel’s time in the wilderness. He alludes to Deuteronomy 32:5 to shape the identity of the Philippians to live blamelessly as ‘children of God . . . in a warped and crooked generation’ (Phil. 2:15). They are to contrast with that particular generation of Israel described as ‘blemished’ (Deut. 32:5, "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("LXX")]), _vm._v(") by being ‘unblemished’ or ‘without fault’ (Phil. 2:15). They are also to avoid ‘grumbling’ (Phil. 2:14), a characteristic of the wilderness generation (e.g. Deut. 1:27). And Paul may draw as well on Deuteronomy 32:47 in his reference to the Philippians holding firmly to ‘the word of life’. Paul also seems to be echoing Daniel 12:3 (‘shine . . . like stars’, Phil. 2:15); and Isaiah 65:23 (‘not . . . labour in vain’, Phil. 2:16); and he uses temple imagery of a ‘drink offering’ and ‘sacrifice and service’ drawn from the Old Testament. This web of allusions and echoes illustrates the storied nature of much of Paul’s use of the Old Testament. Paul can certainly cite or allude to particular scriptural texts, yet even as he does so he could be hearing these in their wider storied setting. And at some points, he is tapping into a part of Israel’s story to shape his audience, as he does in Philippians 2:14–16."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-024-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-024"
    }
  }, [_vm._v("83")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Other recognizable Old Testament allusions or echoes in Philippians include (1) Job 13:16 at Philippians 1:19; (2) possibly Psalm 22:16 at Philippians 3:2; (3) Psalm 69:28 or Daniel 12:1 at Philippians 4:3; and (4) Psalm 145:18 at Philippians 4:5. Fee points to an interesting gap in Old Testament usage in Philippians 3. Specifically, he notes that there is a decided lack of Old Testament argumentation in this ‘polemic’ chapter that begins by addressing the issue of ‘Judaizing’, where we might most expect Paul to marshal it (cf. Galatians and Romans in this regard). Instead, Paul ‘offers himself as an example of one who [held a Judaizing perspective] but had given it all up for Christ’, suggesting that Philippians 3:2–11 should be read less polemically than has been done."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-023-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-023"
    }
  }, [_vm._v("84")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Whether or not Paul expects his primarily Gentile recipients to recognize each of these, often brief, allusions or echoes remains a debated question among New Testament scholars. I suggest that the Philippians who had come to trust in Jesus the Messiah had taken on Scripture (the Old Testament) – its stories and texts – as their own, which were probably communicated primarily aurally through the teaching of Paul and others. And Paul’s preaching would have included in some way the larger story of those Scriptures. So it would not be far-fetched to imagine that the Philippians could have recognized some of Paul’s allusions, as they would have fitted well with Paul’s ways of teaching when he had been with them (see comment on 2:14)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-022-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-022"
    }
  }, [_vm._v("85")])])]), _vm._v(" What we can affirm in any case is that Paul’s communicative intentions can regularly be more clearly discerned if we pay attention to the ways he uses and adapts scriptural texts, stories and images.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("f. The Greek text and its translation")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_c('span', [_vm._v("Although this commentary is not aimed at a scholarly audience, it is necessary to give some attention to textual criticism – that is, making determinations about the original text of Philippians. The earliest extant copy of Philippians comes from around ")]), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("AD")]), _c('span', [_vm._v(" 200 (p")]), _c('sup', {
    staticClass: "sup"
  }, [_vm._v("46")]), _c('span', [_vm._v("). This papyrus manuscript is an important textual witness for Philippians, along with early majuscules (e.g. ")]), _c('span', {
    attrs: {
      "lang": "he-IL",
      "xml:lang": "he-IL"
    }
  }, [_vm._v("א")]), _c('span', [_vm._v(", A, B, D) and quite a number of minuscules. All extant Greek manuscripts witnessing to Philippians include the Pauline letters as an entire group, that is, a corpus (not just Philippians), with the corpus having been brought together sometime during the late first or early second century ")]), _c('span', {
    staticClass: "fsc"
  }, [_vm._v("AD")]), _c('span', [_vm._v(".")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-021-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-021"
    }
  }, [_vm._v("86")])])]), _c('span', [_vm._v(" Bockmuehl notes that the Greek text of Philippians is ‘free from major text-critical uncertainties’, making the work of textual criticism less difficult than in some other parts of the New Testament.")]), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-020-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-020"
    }
  }, [_vm._v("87")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In this commentary, I address eleven text-critical issues in Philippians, with my goal being to focus on any discrepancies among the manuscripts that have particular literary or theological importance (e.g. 1:11; 3:12). I was also aware of the need to explain any major divergences among English translations that arise from textual questions (4:13). For the eleven instances of text-critical discussion in the commentary, see comments (in some cases footnotes) on 1:11, 14; 2:4, 9, 30; 3:3, 12, 16; 4:3, 13, 23.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The translation used in this commentary is the New International Version ("), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _vm._v("), 2011."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-019-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-019"
    }
  }, [_vm._v("88")])])]), _vm._v(" Where I consider likely a different reading from the "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _vm._v(", I note this in the commentary and also explain the rationale for the "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _vm._v("’s rendering. A few particular translation issues warrant some initial comment here and provide examples of the kinds of issues that arise in translating the Greek of the New Testament.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _vm._v(" and almost all other English versions translate "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos ")]), _vm._v("as ‘Christ’ in Philippians and through the New Testament epistles. While I understand the rationale and have no particular issue with this choice, I do find it helpful at points in the commentary to highlight that "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos")]), _vm._v(" is itself a translation of the Hebrew title for the anticipated Jewish ‘Messiah’. I follow Novenson in his demonstration that "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos ")]), _vm._v("sits somewhere between a title and a name; it is an ‘honorific’ which retains at least some of its associations as a title."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-018-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-018"
    }
  }, [_vm._v("89")])])]), _vm._v(" Also, given a tendency in popular parlance to hear Christ as nothing more than Jesus’ (last) name, I have found it helpful to remind myself and my audience of the title, Messiah, and its function as a corporate reality within Pauline theology (see comment on 1:5).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Another translation issue that has been a matter of some debate is how to render the Greek word "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos")]), _vm._v(". In the commentary, I suggest ‘slave’ as a helpful rendering of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos ")]), _vm._v("in 1:1 ("), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _vm._v("’s ‘servant’). While ‘servant’ is possible, I am swayed by Paul’s use of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("doulos ")]), _vm._v("in 2:7 for Christ, who took ‘the very nature of a’ slave. Deeply resonant for Paul and his audience would have been the status connotations of this language. Christ in his exalted status (‘in very nature God’, 2:6) descends to the status of a slave, especially seen in his death on a cross – the form of execution for slaves. Given this status connotation at 2:7, I believe Paul follows in Christ’s footsteps by calling himself and Timothy ‘slaves of Christ Jesus’ (1:1).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("A term that provides something of a challenge to render in English is "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kauchēma")]), _vm._v(" and its verbal cognate, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kauchaomai")]), _vm._v(" (‘boast’ and ‘to boast’). Given that the English word ‘boast’ (traditional rendering) routinely carries a connotation of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("excessive")]), _vm._v(" pride, it can be helpful to use a different English term to render the Greek terms. I have suggested the idea of celebration in the commentary at a few points (see 1:26; 2:16; 3:3; cf. Bockmuehl’s ‘jubilation’)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-017-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-017"
    }
  }, [_vm._v("90")])])]), _vm._v(" For other notable translational issues, see comments on 1:27a ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("politeuomai")]), _vm._v(") and 2:25 ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("leitourgos")]), _vm._v(" and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("leitourgia")]), _vm._v(").")]), _vm._v(" "), _c('h2', {
    staticClass: "ahd",
    attrs: {
      "id": "rch4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/9781789742671_EPUB5#ch4"
    }
  }, [_c('strong', {
    staticClass: "bold"
  }, [_vm._v("4. Theological messages and themes")])])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("The letter to the Philippians has a rich tapestry of theological messages and themes, some fairly distinctive within the Pauline corpus (e.g. joy) and others that correspond to significant themes of his other letters (e.g. the centrality of the gospel and the significance of imitation). There are layers of themes even within a single topic or area, such as Christology or eschatology. For this reason, I delineate six core thematic, theological emphases that serve as constellations or clusters for numerous other motifs, with all of these interwoven across the discourse of Philippians. The six emphases are Christology, eschatology, friendship, communal discernment, unity and resilience in response to suffering, and Christian imitation.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("a. Christology")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("In Philippians, as in Paul’s other letters, Christology is central. Even when Paul is focusing on what we might identify as pragmatic issues (e.g. his imprisonment, details about the dispatch of Timothy and Epaphroditus, his gratitude for their gifts), his central preoccupation with Jesus the Messiah shines through. The arrival of Jesus, Israel’s Messiah, has changed everything. Unsurprisingly then, Paul routinely refers to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos")]), _vm._v(" (the Greek translation of the Hebrew for ‘Messiah’) across Philippians (thirty-seven times), either on its own or with ‘Jesus’ (‘Christ Jesus’ or ‘Jesus Christ’). While it has been argued that "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos ")]), _vm._v("functions more as name than title in Paul, Novenson is persuasive in his argument that the term functions as an ‘honorific’ and so can retain its messianic connotations (see comment at 1:1). If it were simply a (double) name, it would have a set order. Yet in Paul’s other letters, as in Philippians, Paul freely interchanges"), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" Iēsous")]), _vm._v(" (‘Jesus’) and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos")]), _vm._v("."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-016-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-016"
    }
  }, [_vm._v("91")])])]), _vm._v(" In Philippians, Paul prefers the order "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos Iēsous")]), _vm._v(" (thirteen times), yet still uses "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Iēsous Christos")]), _vm._v(" often enough (eight times). So we should take seriously Paul’s thoroughgoing use of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos")]), _vm._v(" to signal ‘the Messiah’. Paul’s Christology centres on the arrival of the promised Messiah, sent by Israel’s God, who has inaugurated Israel’s long-awaited redemption and the restoration of all humanity and all creation. And we can hear overtones of Jesus’ messianic role in many of the references to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos")]), _vm._v(" across Philippians. For example, to ‘preach Christ’ (1:15–17) is to proclaim this messianic story.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("An important way Paul uses "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christos ")]), _vm._v("in Philippians is to reflect the communal reality that those who believe in and follow Jesus the Messiah have been incorporated into his life, death and resurrection. They participate ‘in the Messiah’. Paul signals this spiritual reality with the language "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en Christō ")]), _vm._v("(‘in the Messiah’). For example, in his opening and closing greetings, he addresses ‘God’s [holy] people in Christ Jesus’ (1:1; 4:21). He identifies their standing as a community ‘in the Messiah’, whose life is enfolded into the life of Messiah Jesus. As such, all the Messiah’s benefits belong to them as well (e.g. 3:14; 4:7; see comments on 1:5; 2:1). This communal reality joins together God’s people in unity, just as they enjoy relational union with the Messiah.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul also uses "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kyrios ")]), _vm._v("or ‘Lord’ across the letter to refer to Jesus and to indicate his present exalted status. Paul tells the story of the exaltation to Lordship of Jesus the Messiah in the ‘Christ poem’ of 2:6–11 (on its genre, see 3a). This text is the Christological centrepiece in Philippians. Whether Paul borrows and adapts an early Christian hymn or these stanzas are his own construction (see Context on 2:5–11), this passage provides a distinctive offering for Pauline Christology, especially as it highlights the pre-existence of the Messiah.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The first movement of the poem traces Christ surrendering the advantage of ‘equality with God’ to embrace the identity and condition of humanity (2:6–8). Although some scholars have read 2:6 as describing Christ’s Adamic (human) identity before the fall and dispute that the hymn refers to Christ’s pre-existence, the only allusion to Genesis 1:26 comes in the poem’s second stanza addressing Christ’s incarnation (2:7c/8a)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-015-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-015"
    }
  }, [_vm._v("92")])])]), _vm._v(" The first lines of the poem establish Christ in the ‘nature’ or ‘form’ of God, and at this early point there is little to connect to Adam (though some see an oblique reference in 2:6b). The first stanza (2:6a–7b) suggests a descent in status (an ‘emptying’, 2:7a) of the greatest magnitude – from ‘the form of God’ to ‘the form of a slave’ – the very lowest status."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-014-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-014"
    }
  }, [_vm._v("93")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The poem’s second stanza (2:7c–8d) expresses the particulars of the incarnation. It is at this point we hear an allusion to Genesis, with Christ ‘being made in human likeness’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("homoiōmati anthrōpōn")]), _vm._v(", 2:7; cf. "), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("LXX")]), _vm._v(" Gen. 1:26). In the incarnation, Christ ‘humbled himself’, embracing his divinely appointed mission even to the point of death (2:8). ‘Christ was obedient to God in his service to humanity.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-013-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-013"
    }
  }, [_vm._v("94")])])]), _vm._v(" The last line of the second stanza confirms the slavery metaphor of 2:7 by indicating the kind of death of the Messiah: ‘even death on a cross’. Crucifixion was quintessentially the form of execution intended for slaves in the Roman-occupied world.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The second half of the Christ hymn (2:9–11) portrays the exaltation and Lordship of Christ. God’s exaltation of Messiah Jesus after his self-humbling puts him in the ‘highest place’, as God grants him ‘the name that is above every name’ (2:9). That phrase alludes to the divine name (Yahweh) used across Isaiah 45:18–24 and heard in language borrowed from Isaiah 45:23, where it is affirmed that, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("before Yahweh")]), _vm._v(", ‘every knee will bow . . . every tongue will acknowledge God’ ("), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("LXX")]), _vm._v("). Now, in Philippians 2:10–11, it is "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("before Jesus")]), _vm._v(" that ‘every knee should bow . . . and every tongue acknowledge’ him. Jesus is exalted to the place of universal authority and so is worthy of worship, alongside Israel’s God (see comment on 1:1). This is the import of the pronouncement ‘Jesus Christ is Lord’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kyrios")]), _vm._v(", 2:11). Lordship, through this Isaiah allusion, speaks to Christ’s participation in ‘the divine sovereignty’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-012-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-012"
    }
  }, [_vm._v("95")])])]), _vm._v(" The Messiah, who began in the place of ‘equality with God’, became a human being (Jesus of Nazareth). His fully faithful life and death resulted in God’s exaltation of Messiah Jesus to the highest place: to the place of Lordship over all things.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The surprising conclusion to the Christ poem is not the return of Christ to the place of divine authority – a place the poem has affirmed as Christ’s starting point (2:6); it is that a human being (Jesus of Nazareth) is exalted to the place of divine authority and receives the name ‘Lord’. The exaltation of Jesus Christ to universal Lordship is an affirmation of his divine–human identity.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Christ’s exaltation to universal Lordship also presses against false claims of lordship that would have been pervasive in Philippi, in its Roman-saturated culture. If Jesus is "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("the ")]), _vm._v("exalted Lord, then Caesar cannot be lord in spite of his claims to lordship (see comment on 2:10–11). Paul’s consistent use of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("kyrios ")]), _vm._v("to describe Messiah Jesus affirms this truth (e.g. 1:2; 2:11, 19; 3:8, 20; 4:23). Another implicit critique of the power of Caesar may be implied in Paul’s use of ‘Saviour’ for Jesus ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("sōtēr")]), _vm._v(", 3:20), especially since this term does not occur much elsewhere in the Pauline epistles and given Roman claims that the emperor was the people’s saviour (see comment on 3:20)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-011-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-011"
    }
  }, [_vm._v("96")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("While Paul employs the ‘Christ poem’ within the flow of Philippians 2 to inform key values, such as humility, to be embraced by the Christian community (2:5; cf. 2:1–4), we should not lose sight of its importance for Pauline Christology. Christ, though fully God, embraced becoming human – a meteoric descent of status, even to the point of the most shameful kind of death. As Paul affirms elsewhere in the letter, Christ’s death and resurrection paves the way for the present salvation and future resurrection of believers. It is the exalted and ruling Christ who ‘will transform our lowly bodies so that they will be like his glorious body’ (3:21). His exaltation in resurrection makes possible their own future resurrection (3:10–11).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("A final point about Paul’s Christology in Philippians is its profound relationality, an insight drawn from Paul’s autobiographical descriptions in 3:7–11. There, Paul expresses that ‘knowing Christ’ (3:8) is his greatest desire and focus. This participation in the entire scope of Christ’s story – his suffering as well as his resurrection and exaltation – echoes that story as rehearsed in the Christ poem (2:6–11). For Paul, knowing Christ, a reality he can also describe as ‘gain[ing] Christ’ and being ‘found in him’ (3:8–9), is the greatest of all experiences. Paul also writes of Christ’s affection or compassion that propels his own affections (1:8), Christ’s love that provides the greatest comfort (2:1), Christ’s nearness to believers (4:5), and the grace that Christ gives (4:23; cf. 1:2). This reality of relationship with Christ causes Paul to celebrate (‘boast in’) Christ, together with his Philippian sisters and brothers (1:26; 3:3). Relationship with Christ brings Paul ultimate joy.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("b. Eschatology")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("By placing the arrival and work of Christ at centre for understanding theological history, Paul’s eschatology in Philippians fits closely with the eschatology offered in his other letters. For Paul, the arrival of Jesus, Israel’s Messiah, is the fulcrum of salvation history. This is at the heart of Paul’s eschatology – the assumption that in Jesus the last days have begun. What came before, Paul can express in terms of the evil age or the time of ‘the flesh’, the age when sin wielded inordinate power over humanity. Yet Christ’s death has rescued believers ‘from the present evil age’ (Gal. 1:4). His death and resurrection have inaugurated the ‘age to come’ (cf. Eph. 1:21), so that Paul can refer to Christians as those ‘on whom the culmination of the ages has come’ (1 Cor. 10:11).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In Philippians, Paul refers very little to the age of the ‘flesh’ that is passing away. Only at 3:3–6 does Paul refer to the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("sarx ")]), _vm._v("or ‘flesh’ to indicate his past confidence in his ethnic and religious heritage, a confidence that is no longer appropriate in the light of the arrival of the Messiah.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul focuses his eschatology in Philippians on the present time of the Messiah, which has been transformed through Jesus’ death, resurrection and exaltation to Lordship (2:6–11; see 4a). Paul and the Philippian believers are living in the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("already–not yet ")]), _vm._v("of God’s kingdom. Their shared life in the Messiah maps on to the eschatological terrain of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("already")]), _vm._v(", as they participate in the current blessings of the time of the Messiah. And they await what is still "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("not yet – ")]), _vm._v("their own resurrection and the completion of God’s work in them at the ‘day of Christ Jesus’ (1:6).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul’s recurring language of the ‘day of Christ Jesus’ (1:6; cf. 1:10; 2:16) fits with Paul’s similar and flexible language across his letters to indicate the still-future arrival of complete salvation for believers (e.g. 1 Cor. 1:8; 5:5; 1 Thess. 5:2; cf. Rom. 2:5, 16). This language resonates conceptually with the ‘day of the Lord’ motif that emerges in Jewish prophetic literature and signals a future day when Israel’s God will bring restoration and judgment (e.g. Joel 2:31; 3:14; Zech. 13:1; 14:1). Paul focuses the prophetic language of the ‘day of the Lord’ Christologically to reflect his conviction that the restoration begun in Jesus will be completed on that final day – the day of the Messiah. In the light of that certain future reality, believers can look ahead to their salvation (1:28) without fearing death (1:21–23).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul especially focuses attention on the eschatology of ‘already but not yet’ in Philippians 3:2–21, a section of the letter that illuminates how relationship with Christ changes everything. Paul compares what he had previously found valuable in his heritage and religious commitments (3:4–6) with his present experience of knowing Christ and participating in Christ’s sufferings as well as in his resurrection (3:7–10). In reality, there is no comparison; for Paul, all else is worthless when compared with ‘the surpassing worth of knowing Christ Jesus my Lord’ (3:8). This present reality of knowing Christ also leads to the future possibility of ‘attaining to the resurrection from the dead’ (3:11). Paul views all else as inconsequential when compared with his goals of knowing Christ and reaching the day of resurrection.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In Philippians 3:12–14, Paul exemplifies a finely tuned balance of living in the time between the already and the not yet. He concedes more than once that he has not yet arrived at the goal of resurrection and exaltation, which still lies in the future (cf. 3:20–21). Yet this only seems to invigorate Paul’s eagerness to reach his goal; he earnestly presses on ‘to take hold of that for which Christ Jesus took hold of me’ (3:12). And he calls the Philippians to do the same – to live in a way consistent with what they ‘have already attained’ (3:16). For Paul, this eschatological mindset is a key sign of Christian maturity (3:15).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Towards the end of Philippians 3, Paul provides a warning about some believers (or former believers) who are now living as ‘enemies of the cross’ (3:18). Paul goes on to describe this group as those who have abandoned the eschatological mindset he has been characterizing. In contrast to a true eschatological mindset, the mindset of these ‘enemies of the cross’ (i.e. antagonistic to suffering) is focused on ‘earthly things’ (3:19). To combat any similar capitulation on their part, Paul exhorts the Philippians to embrace their true eschatological identity: ‘our citizenship is in heaven’ (3:20). He points them to the still future arrival of Jesus their Saviour and their own final-day resurrection (3:21). Paul will, in chapter 4, encourage them that ‘the Lord is near’ (4:5), likely signalling both the nearness of the day of the Lord Jesus and his closeness to believers in the present.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul’s eschatologically focused mindset provides the capacity to reframe life in the present, even and especially the suffering that Paul and the Philippians are experiencing (1:28–30). First, suffering fits within Paul’s eschatological framework as a sign of the present experience of the "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("not yet")]), _vm._v(". This means that suffering has a time stamp on it. When ‘the day of Christ’ arrives, all suffering will be past tense. From the vantage point of that final day, ‘our present sufferings are not worth comparing with the glory’ that awaits (Rom. 8:18). Paul can even refer to suffering as ‘light and momentary troubles’ (2 Cor. 4:17) from that future perspective. Suffering is not a virtue or a ‘good’ to be carried into the eschaton. It is a remnant of the present evil age that is already passing away in Christ.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Second, Paul considers any suffering that comes from remaining loyal to Christ and his gospel (1:27–28) as integral to participating in Christ’s sufferings (3:10). Participation in the Messiah involves sharing in his entire story – suffering and resurrection, death and life (2:6–11; 3:10–11). There is no embracing a theology of glory without joining in a theology of the cross (Rom. 8:17; 2 Cor. 1:5). As Christ’s identification with humanity involved sacrifice for the sake of mission as well as service to others (2:7–8), so also believers’ identification with Christ involves these same dispositions and realities. ‘The thought of a spiritual union with Christ in His death and resurrection . . . is at the very heart of Paul’s experience and teaching.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-010-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-010"
    }
  }, [_vm._v("97")])])]), _vm._v(" In this light, Paul can even refer to the Philippians’ suffering because of their allegiance to Christ as something that has been ‘granted’ to these believers (1:29). To be joined in union with Christ to this extent – to suffering for and with him – can be understood as a gift.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Finally, Paul considers ministry, what he calls ‘the work of the Messiah’ (2:30, my translation), worth any suffering it entails. If, in the final day, the Philippians have lived out their identity as God’s children and have been a missional light in their own setting (2:15–16), Paul will consider it a joy to have experienced any suffering necessary in his ministry among them: ‘even if I am being poured out like a drink offering on the sacrifice and service coming from your faith, I am glad’ (2:17). Paul reflects here the integral connection between opposition or suffering and the work of the gospel. As Stubbs suggests, Paul ‘does not sacralize suffering. Instead, he collapses the distinction between conviction and action.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-009-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-009"
    }
  }, [_vm._v("98")])])]), _vm._v(" If Paul and the Philippians put into action their allegiance to Jesus, they will in all likelihood experience suffering for that conviction.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("c. Friendship")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("In considering the particular kind of letter that Philippians is, some have identified it as a letter of friendship (see 3a). Whether or not there is adequate evidence for this specific categorization, Paul’s use of friendship motifs, corresponding to the Graeco-Roman topic of friendship, is unmistakable. The fabric of friendship in the letter is most evident in the frequent use of the language of "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("koinōnia ")]), _vm._v("(‘participation’; 1:5; 2:1; 3:10) and its cognates "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("synkoinōnos ")]), _vm._v("(1:7),"), _c('em', {
    staticClass: "italic"
  }, [_vm._v(" synkoinōneō ")]), _vm._v("(4:14) and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("koinōneō")]), _vm._v(" (4:15). Paul uses this constellation of terms to highlight the benevolent and reciprocal relationship he shares with his brothers and sisters in Philippi (1:5, 7; 4:14, 15). It is a relationship grounded in the participation and union they all have experienced in Christ (2:1; 3:10) and through the Holy Spirit (2:1). As Fowl expresses it, ‘We are called into friendship with each other because of our common friendship in Christ.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-008-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-008"
    }
  }, [_vm._v("99")])])]), _vm._v(" Friendship with fellow believers is built on believers’ friendship with God received in the representative work and example of Messiah Jesus (2:5–11). Paul highlights union with Christ in Philippians by using the language of being ‘in Christ’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en Christō")]), _vm._v("; e.g. 1:1; 4:21; see 4a) and in expressions of his own deep desire to ‘know Christ – yes, to know the power of his resurrection and participation in his sufferings’ (3:10).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul’s letter to the Philippians strikes a tone of friendship, with language of affection and longing permeating the letter (1:7–8; 2:26; 4:1). Paul’s consistent expressions of joy contribute to this warm, relational tone, as he rejoices at the thought of his ‘dear friends’ in Philippi (1:4; 2:17; 4:1, 10) and frequently prompts them to rejoice from their side of the friendship (2:18, 29; 3:1; 4:4). For Paul, ‘the proper working of Christian friendship’ produces joy."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-007-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-007"
    }
  }, [_vm._v("100")])])]), _vm._v(" Yet it is not only Paul’s joy that fits well the theme of friendship. His own ‘relational anxiety’ points to his deep friendship with the Philippians, and with Timothy and Epaphroditus (see Theology on 2:19–30). This anxiety arises from the concern for a friend’s well-being, including that person’s emotional and spiritual welfare (e.g. 2:20, 26, 27–28). The depth of the relationship shared by Paul, Timothy, Epaphroditus and the Philippians brings with it concern for the circumstances and well-being of the other.")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("The friendship between Paul and the Philippians centres around the gospel of Christ, a reality made especially clear at the conclusion of the letter (4:10–20). There, Paul (implicitly) conveys his gratitude to the Philippian congregation for the gift they had sent to him through Epaphroditus. For Paul, this tangible support for him while in prison is an important expression of their common life together – their ‘partnership in the gospel’ (1:5). So while Paul uses what we might call more transactional language of finance in 4:14–18, it is nested within a theological framework that places God in Christ at the centre of Christian relationship (4:18–20). Christians are joined in profound friendship anchored in the gospel and in their friendship with God.")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("d. Communal discernment")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Paul guides the Philippians towards a disposition of communal discernment as they reorient their perspective through the lens of the gospel. This is the shared mindset Paul refers to across the letter – a mindset that involves attitudes and emotions, dispositions and thinking."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-006-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-006"
    }
  }, [_vm._v("101")])])]), _vm._v(" Early on, Paul has offered a prayer for the Philippian community to ‘discern what is best’ (1:10). This focus on discernment reappears in the exhortation towards a shared mindset that is oriented around Christ’s own mindset ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("phroneō")]), _vm._v(", 2:5). The Philippian believers are called to consider together how to live with one another ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("phroneō")]), _vm._v(", 2:2; 4:2) and in their particular cultural location (4:8) in a way that coheres with the story of Christ – with the gospel. Fowl suggests the language of ‘practical reasoning’ for "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("phroneō")]), _vm._v(" and describes it as a ‘pattern of practical reasoning involv[ing] a common perspective on their situation and how it fits into the divine economy and the practical implications of that perspective’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-005-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-005"
    }
  }, [_vm._v("102")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("In addition to "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("phroneō")]), _vm._v(", Paul uses two other words in Philippians that contribute to this constellation of terms around the concept of discernment: "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("hēgeomai ")]), _vm._v("(‘consider’, 2:3, 6, 25; 3:7–8) and "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("logizomai")]), _vm._v(" (‘consider’, 3:13; cf. 4:8). Each term is used to promote a reorientation from prior dispositions, actions and ways of thinking towards a Christ-focused perspective. Paul provides his own example of a changed point of view, as he narrates past ways of understanding his privileges and accomplishments (3:4–6) and compares them with his present preoccupation with knowing and gaining Christ and arriving at the day of resurrection (3:7–11). He ‘considers’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("hēgeomai")]), _vm._v(", used three times in 3:7–8) his life and goals quite differently from his current vantage point of being ‘in the Messiah’ (‘in him’, 3:9). Paul’s temporal location in the time after the Messiah’s arrival but before the final consummation means he discerns or carefully ‘considers’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("logizomai")]), _vm._v(", 3:13) that location in the way he lives. For Paul, Christ Jesus provides the supreme example of a mindset of discernment, as Christ considered ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("hēgeomai")]), _vm._v(") taking on human likeness more important than capitalizing on the advantages of his divine prerogatives and status (2:6).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Towards the end of the letter, Paul calls the Philippians to a posture of discernment related to their cultural context. They are to weigh ‘the good’ on offer in that context (4:8), and they are to do so in the light of the gospel (4:9)."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-004-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-004"
    }
  }, [_vm._v("103")])])]), _vm._v(" Paul again uses the verb "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("logizomai")]), _vm._v(" for the activity of considering or weighing what is ‘excellent or praiseworthy’ (4:8; see comment there). This is a communal task, with the Philippian congregation discerning together God’s wisdom and direction for them. And they are to seek discernment from a posture of unity, from a shared mindset. Again, it is the gospel – the story of Jesus – that grounds this common mindset. Paul entrusts this communal discernment to God, who will bring wisdom and clarity to the community as its members grow in maturity (3:15).")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("e. Unity and resilience in suffering")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("As is already evident from the discussion of communal discernment (4d), a prominent theme in Philippians is communal unity. This central theme is integrally connected to Paul’s initial exhortation of the letter to live out singular loyalty to the gospel (1:27). By doing so, the Philippian believers will be able to ‘stand firm in the one Spirit, striving together as one for the faith of the gospel’ (1:27). Only a profound unity of focus and direction can result in the entire church working together as if they were one person. In a series of exhortations, Paul demonstrates how such singular unity is possible: through a shared mindset and affection informed by the virtue of humility that highly values others and looks out for their interests (2:2–4). This ‘shared mindset’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("to auto phronēte")]), _vm._v(", 2:2; see 4d) is based on imitation of Christ, whose mindset was infused with humility and was worked out in other-focused giving and action (2:5–8). Paul draws on the same language ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("to auto phronēte")]), _vm._v(") later in the letter, to implore two co-workers, Euodia and Syntyche, towards a shared mindset ‘in the Lord’ (4:2). Paul also warns about practices that disrupt unity and so should be avoided, such as ‘grumbling’, ‘arguing’ (2:14), and placing confidence in anything other than Christ himself (3:3, 7–8). Dispositions that contribute to unity include an eschatologically oriented mindset (3:12–21) and joy (2:18; 4:4).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul grounds his exhortations to unity in the unity believers already share ‘from being united with Christ’ (translating "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en Christō")]), _vm._v(", 2:1). This phrase, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en Christō")]), _vm._v(", points to the present reality of the believer’s union with Christ, which forms the basis for the Christian’s unity with all other believers. All who trust the Messiah are together ‘in the Messiah’ ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("en Christō")]), _vm._v("). And all who trust in the Messiah share in the ‘one Spirit’, who makes possible Christian oneness (1:27; 2:1).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("From the outset, the theme of Christian unity is connected to resilience in suffering. By ‘striving together as one for the faith of the gospel’, the Philippian congregation will have the capacity to address any fear or anxiety that arises from opposition (1:27–28). Remaining loyal to Christ and the gospel in first-century Roman Philippi was no easy task. Within a polytheistic landscape such as Philippi, people would divide their allegiances among any number of gods, including giving attention to the demands of the imperial cult. In such a setting, civic and religious expectations promoted loyalty to and worship of multiple deities, even if someone might maintain a preference for or special connection to one particular god. What was little understood or accepted was allegiance to only one god (and that one not recognized as part of the Greek or Roman pantheon), as promoted by those who worshipped Messiah Jesus exclusively. When Christians proclaimed Christ and Christ alone as Lord, they were rejecting a primary religious and civic duty. So it would be unsurprising that opposition and even persecution could follow, some of which may very well have been economic in form."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-003-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-003"
    }
  }, [_vm._v("104")])])]), _vm._v(" As Cohick suggests, ‘Confessing Christ was one way [for service providers and shopkeepers] to scare away patrons and often resulted in the believer leaving the pagan trade guild or being ostracized from others doing similar work.’"), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-002-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-002"
    }
  }, [_vm._v("105")])])])]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Although Paul explicitly mentions the Philippians’ suffering due to such opposition only once in the letter (1:28), the motif of suffering emerges elsewhere. First, Paul identifies with their suffering, suggesting that their struggle is similar to his own (1:30). Second, Paul portrays a counter-example at 3:18–19 to the singular allegiance he calls the Philippians to embrace. There, he references ‘enemies of the cross’ – an image that seems to point to some who have compromised their loyalty to Christ so as to avoid opposition and suffering. With their ‘mind . . . set on earthly things’ (3:19), they have lost sight of the supreme value of Christ and his gospel (3:8), which would help them not only to endure suffering but to see it as a kind of gift (1:29) of deeper identification with Christ (3:10).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Resilience in suffering, then, comes from the community having a right mindset about what is supremely valuable – ‘knowing Christ’ (3:8) and identifying with him in all ways, including participating in his sufferings (3:10). Resilience in suffering also comes from putting suffering in perspective. While it may be a present reality for many or most of the Philippian believers, it is not their final state. By having an eschatological mindset, they can see ahead to their transformation, resurrection and exaltation (3:11, 20–21; see a fuller discussion of suffering at 4b). Finally, and to return to the theme of unity, the burden of suffering is lightened with the help and solidarity of a united community (1:27–30). Paul paints a vivid picture of such unity of purpose, even if attended by opposition and suffering, when he writes, ‘even if I am being poured out like a drink offering on the sacrifice and service coming from your faith, I am glad and rejoice with all of you’ (2:17).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("What emerges in Philippians is a portrait of a church being encouraged to endure suffering, when it is necessary for remaining true to the gospel and to Christ, who himself provides an example of missional suffering that the Philippians are to be ready to emulate (2:8). With a shared mindset grounded in Christ’s own mindset, the Philippian believers will be able to ‘stand firm in the Lord’ (4:1).")]), _vm._v(" "), _c('h2', {
    staticClass: "bhd"
  }, [_c('span', {
    staticClass: "bold-italic"
  }, [_vm._v("f. Christian imitation")])]), _vm._v(" "), _c('p', {
    staticClass: "tx"
  }, [_vm._v("Imitation was an important pedagogical and discipleship practice in the first-century world. And it fitted equally well within both Graeco-Roman and Jewish contexts. As Keown notes, in the ancient world, ‘before the printing press and electronic media, life was taught more by way of models than simply instruction’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-001-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-001"
    }
  }, [_vm._v("106")])])]), _vm._v(" Paul emphasizes imitation both implicitly and explicitly in Philippians. Implicitly, he provides a number of exemplars who model a way of life for the congregation. His commendation of Timothy provides a path to follow, especially Timothy’s pattern of looking out for the interests of others, which, Paul claims, corresponds to looking out for the interests of Christ himself (2:20–21; cf. 2:4). Timothy also models serving in the work of the gospel (2:22). Epaphroditus is characterized similarly (2:25–30) and so offers another example for the Philippians to follow. Additionally, the Philippians are to emulate Paul’s mindset, especially as he models the reorientation of his entire existence towards the ultimate value of knowing Christ and being found in Christ (3:4–14).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("When Paul calls the Philippian church to follow Christ as exemplar his message becomes more explicit: ‘In your relationships with one another, have the same mindset as Christ Jesus’ (2:5). This community of believers, residents of a Roman colony, are to embrace the countercultural disposition of humility (2:3–4), and they are to do so precisely because their exalted Messiah and Lord set the pattern of humility and renunciation of status on behalf of humanity (2:6–11). Jesus is the role model par excellence; only as other believers imitate him are they worthy of emulation (cf. 3:10–11 with 2:6–11).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("Paul also expressly exhorts the Philippian believers to follow his lead (3:17a); he is not shy about calling for imitation of his own example. While this might sound arrogant to modern sensibilities, it would have had no such negative connotation for Paul and his audience. Not only does Paul regularly call his churches to imitate himself (1 Cor. 4:16; 11:1; 2 Thess. 3:7–9; cf. 1 Thess. 1:6), he makes it clear that he does so only as he himself is imitating Christ (1 Cor. 11:1; Phil. 3:10). And, as he emphasizes towards the end of Philippians, believers are to follow him in the ways of the gospel (what they have ‘learned or received or heard from me, or seen in me’, 4:9). The Philippian believers are to imitate Paul, as well as all others who live out the gospel and its values (3:17b; cf. 2:29).")]), _vm._v(" "), _c('p', {
    staticClass: "txt"
  }, [_vm._v("As the foundation for imitation of Christ and of Christian exemplars in Philippians, Christ himself makes the move to imitate humanity by taking upon himself the human condition (2:7–8). In the Christ poem, Paul portrays Christ as ‘shar[ing] fully in the desperate contingency, suffering, and death of Adam’s heirs’."), _c('sup', {
    staticClass: "sup"
  }, [_c('span', {
    attrs: {
      "id": "footnote-000-backlink"
    }
  }, [_c('a', {
    staticClass: "_idFootnoteLink _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-000"
    }
  }, [_vm._v("107")])])]), _vm._v(" Because of Christ’s identification with humanity, believers are brought into Christ’s own story of humility and exaltation, suffering and resurrection. Because of Christ’s imitation of humanity, humanity is empowered to imitate Christ.")]), _vm._v(" "), _c('hr', {
    staticClass: "rule"
  }), _vm._v(" "), _c('div', {
    staticClass: "_idFootnotes"
  }, [_c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-106"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-106-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("1.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For examples, see Bockmuehl, pp. 42–45; Fowl, pp. 1–8; Tamez, pp. 1–3.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-105"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-105-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("2.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Brown and Fox, ‘Hermeneutics’, forthcoming.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-104"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-104-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("3.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Bockmuehl, p. 43.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-103"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-103-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("4.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For a discussion of the genre and usefulness of Acts as history, see Keener, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Acts")]), _vm._v(", pp. 1–29.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-102"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-102-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("5.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, p. 44. McCaulley offers some examples in "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Reading while Black")]), _vm._v(", pp. 18, 168–169.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-101"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-101-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("6.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Brown ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Gospels as Stories")]), _vm._v(", p. 193) defines the implied author as the ‘textually derived construct for understanding the author, which is distinct from the empirical (flesh-and-blood) author. The implied author is the writing persona presupposed in the [text] itself.’")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-100"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-100-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("7.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Childs’ discussion on the relationship between these two in "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Reading Paul")]), _vm._v(", pp. 1–10.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-099"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-099-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("8.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, ‘History’, p. 65.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-098"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-098-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "nl2"
  }), _vm._v("9.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Osiek, p. 78. Examples of interpreting conflict behind some or much of the letter include Capper, ‘Dispute’, and Peterlin, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Disunity")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v(".")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-097"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-097-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("10.")])]), _c('span', {
    staticClass: "nl2"
  }), _c('span', [_vm._v("Thurston ")]), _c('span', [_vm._v("and Ryan")]), _c('span', [_vm._v(", p. 16.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-096"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-096-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("11.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For a fuller discussion, see Brown, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Scripture")]), _vm._v(", pp. 179–186.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-095"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-095-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("12.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Sometimes ‘literal’ is used to refer to a strict or wooden rendering of the Greek intended to visualize Greek word order or phrasing in English. At a few points in the commentary, I use the language ‘rendered woodenly’ to illuminate Greek grammatical issues.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-094"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-094-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("13.")])]), _c('span', {
    staticClass: "nl2"
  }), _c('span', [_vm._v("Masculine linguistic gender was used for the generic in Koine Greek to indicate a person, either male or female, or for a group that includes both genders.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-093"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-093-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("14.")])]), _c('span', {
    staticClass: "nl2"
  }), _c('span', {
    staticClass: "text"
  }, [_vm._v("In fact, we could note that Paul uses this term in Philippians ‘non-literally’, that is, metaphorically, since he is not claiming that the Philippians are his physical relatives but his spiritual (metaphorical) siblings; i.e. family members with kinship ties.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-092"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-092-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("15.")])]), _c('span', {
    staticClass: "nl2"
  }), _c('span', [_vm._v("Barr, ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Semantics")]), _c('span', [_vm._v(", pp. 218–222.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-091"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-091-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("16.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Gupta, pp. 9–10.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-090"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-090-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("17.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Brown and Fox, ‘Hermeneutics’, forthcoming.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-089"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-089-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("18.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Bockmuehl’s caution about such a narrow endeavour (pp. 41–42).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-088"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-088-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("19.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See, for example, Bockmuehl, pp. 43–45; Bockmuehl, ‘History’; and Silva, pp. 26–34.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-087"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-087-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("20.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Brown and Roberts, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Matthew")]), _vm._v(", p. 381.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-086"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-086-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("21.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Many of the ideas explored and detailed in parts 2 and 4 of this introduction are based on my work in Brown, ‘Philippians’ (forthcoming).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-085"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-085-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("22.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For a recent proposal that accounts for the significant differences among all thirteen letters by proposing a number of ‘Pauline schools’ involving relevant co-sponsors and amanuenses that worked with Paul, see Johnson, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Constructing Paul")]), _c('span', [_vm._v(", pp. 90–92.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-084"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-084-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("23.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Schnabel, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Mission")]), _vm._v(", p. 1152.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-083"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-083-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("24.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, pp. 25–26.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-082"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-082-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("25.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes approximates the town’s population from its geographic size and a comparison with the known population of Pompeii ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", pp. 44–45). Many people would also have populated the countryside outside the town.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-081"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-081-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("26.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Cohick, p. 16.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-080"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-080-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("27.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Martin, p. 16.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-079"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-079-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("28.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", p. 74. By ‘Romans’, Oakes includes primarily ‘grandchildren and great-grandchildren of veterans or peasant colonists’, although a small number of Greeks who had been granted Roman citizenship could function within this category (p. 71).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-078"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-078-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("29.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bormann, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippi")]), _vm._v(", p. 41;"), _c('span', [_vm._v(" Heen, ‘Phil 2:6–11’, pp. 135–136.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-077"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-077-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("30.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bormann, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippi")]), _vm._v(", p. 44;"), _c('span', [_vm._v(" Cohick, p. 18.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-076"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-076-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("31.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Heen, ‘Phil 2:6–11’, pp. 135–136.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-075"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-075-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("32.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Nikki, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Opponents")]), _vm._v(","), _c('span', [_vm._v(" p. 66; see Bormann, ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippi")]), _c('span', [_vm._v(", p. 42.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-074"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-074-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("33.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fowl highlights these first three points of connection (p. 13).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-073"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-073-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("34.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Thurston and Ryan, pp. 19–21.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-072"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-072-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("35.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Hooker, p. 471.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-071"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-071-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("36.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Keener, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Acts")]), _vm._v(", pp. 385–387;"), _c('span', [_vm._v(" Cohick, p. 16.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-070"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-070-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("37.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", pp. 61–62) estimates between 50 and 100.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-069"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-069-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("38.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Gehring, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("House Church")]), _vm._v(", p. 141.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-068"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-068-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("39.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Gehring, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("House Church")]), _vm._v(","), _c('span', [_vm._v(" pp. 131–132.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-067"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-067-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("40.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", p. 61;"), _c('span', [_vm._v(" Hellerman, p. 78.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-066"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-066-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("41.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", pp. 61, 63. Here, Oakes includes under ‘Greek’ a number of ethnicities whose language was Greek, including Thracian and Macedonian (p. 18).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-065"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-065-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("42.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", p. 61.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-064"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-064-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("43.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, p. 28.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-063"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-063-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("44.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("A view held by Thielman, Osiek, and Bird and Gupta, among others.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-062"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-062-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("45.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, p. 36.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-061"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-061-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("46.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, pp. 277–278.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-060"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-060-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("47.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Rapske, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Roman Custody")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v(",")]), _vm._v(" "), _c('span', [_vm._v("pp. 20–35; also Tamez, pp. 12, 17. See Tamez, pp. 11–18, for a detailed discussion of these types of custody and Paul’s experiences as a prisoner while writing Philippians.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-059"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-059-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("48.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Rapske, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Roman Custody")]), _vm._v(", p. 31; Tamez, p. 12.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-058"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-058-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("49.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Klauck, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Letters")]), _vm._v(", p. 63.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-057"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-057-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("50.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Holloway, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Consolation")]), _vm._v(", pp. 45–48.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-056"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-056-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("51.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Flemming, p. 29.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-055"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-055-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("52.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Although Nikki ("), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Opponents")]), _vm._v(", p. 138) argues they are the same group (‘Jewish Christ-believers’) referenced in 3:2 and 3:18–21.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-054"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-054-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("53.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", pp. 89–102.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-053"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-053-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("54.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For an argument against reading 3:12, 15 as an allusive reference to a perfectionist element within the Philippian church (another set of ‘opponents’), see comment on 3:12.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-052"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-052-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("55.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Nikki, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Opponents")]), _vm._v(", pp. 216–218; Silva also considers this a good possibility and identifies the group as Judaizers.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-051"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-051-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("56.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("E.g. Osiek, pp. 102–103; Smit, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Paradigms")]), _vm._v(", p. 75.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-050"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-050-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("57.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Stowers, ‘Friends’, pp. 109–113. See Fee, pp. 2–14.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-049"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-049-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("58.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("On the friendship theme in Philippians, see 4c.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-048"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-048-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("59.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Alexander, ‘Philippians’, p. 95.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-047"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-047-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("60.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Holloway, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Consolation")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v(".")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-046"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-046-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("61.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Klauck, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Letters")]), _vm._v(","), _c('span', [_vm._v(" p. 340.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-045"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-045-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("62.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("E.g. Fee, p. 3; Holloway, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Consolation")]), _vm._v(","), _c('span', [_vm._v(" pp. 161–164.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-044"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-044-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("63.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee (p. 41) calls it ‘exalted – even poetic – "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("prose")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v("’.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-043"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-043-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("64.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, p. 116; Fowl, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Story")]), _vm._v(", pp. 16–17; Hansen, p. 122. For the argument against identifying 2:6–11 as a pre-existing hymn, see Fowl, pp. 108–113.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-042"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-042-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("65.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("This translation of 2:6–8 is my own (see Context for 2:6–8).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-041"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-041-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("66.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Brown, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Scripture")]), _vm._v(","), _c('span', [_vm._v(" pp. 145–146. Bockmuehl (p. 119) suggests that the ‘several untypical ideas and terms in the passage . . . could well be the result of its poetic style’ (cf. 1 Cor. 13).")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-040"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-040-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("67.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("For other repeated terms that provide structural clues for the Christ poem, see the commentary.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-039"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-039-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("68.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Cunningham, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("To Teach")]), _vm._v(", p. 14. See deSilva, ‘Appeals’, pp. 248–249; and Smit, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Paradigms")]), _vm._v(", pp. 31–33.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-038"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-038-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("69.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Boyce, ‘Rhetoric’, p. 205.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-037"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-037-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("70.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("DeSilva, ‘Appeals’, p. 245. Also, Boyce, ‘Rhetoric’, p. 203.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-036"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-036-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("71.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Boyce, ‘Rhetoric’, p. 203.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-035"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-035-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("72.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Kennedy, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Rhetorical Criticism")]), _vm._v(", p. 15.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-034"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-034-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("73.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Cunningham, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("To Teach")]), _vm._v(", p. 18.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-033"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-033-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("74.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("DeSilva, ‘Appeals’, p. 247.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-032"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-032-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("75.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Holloway, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Consolation")]), _vm._v(", p. 55.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-031"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-031-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("76.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Reumann, pp. 16–17.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-030"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-030-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("77.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Achtemeier, ‘Oral Environment’, p. 26. For further discussion of this shift of tone, see comment at 3:1.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-029"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-029-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("78.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, p. 17. See Context on 4:10–20.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-028"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-028-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("79.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fowl, p. 192.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-027"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-027-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("80.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("And framed by the letter’s introductory and closing greetings and well-wishes in 1:1–11 and 4:21–23.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-026"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-026-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("81.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Smit, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Paradigms")]), _vm._v(", p. 4.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-025"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-025-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("82.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bauckham, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Jesus")]), _vm._v(", p."), _c('span', [_vm._v(" 38.")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-024"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-024-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("83.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Brown, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Gospels as Stories")]), _vm._v(", pp. 112–116.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-023"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-023-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("84.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, p. 17.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-022"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-022-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("85.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Brown, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Gospels as Stories")]), _vm._v(", p. 110.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-021"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-021-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("86.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See Fee, p. 23.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-020"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-020-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("87.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, p. 40.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-019"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-019-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("88.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Translations of the Septuagint are my own.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-018"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-018-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("89.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Novenson, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christ")]), _c('em', {
    staticClass: "italic"
  }, [_vm._v(". ")]), _vm._v("See comment on 1:1.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-017"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-017-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("90.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bockmuehl, p. 95.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-016"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-016-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("91.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Novenson, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Christ")]), _vm._v(", pp. 91–92, 99–101.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-015"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-015-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("92.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, p. 209.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-014"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-014-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("93.")])]), _c('span', {
    staticClass: "nl2"
  }), _c('span', [_vm._v("My translations (see ")]), _c('span', {
    staticClass: "SmallCaps"
  }, [_vm._v("NIV")]), _c('span', [_vm._v(" footnotes).")])])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-013"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-013-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("94.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Stubbs, p. 371.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-012"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-012-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("95.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Bauckham, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Jesus")]), _vm._v(", p. 200.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-011"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-011-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("96.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("See examples in Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", pp. 138–140.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-010"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-010-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("97.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Martin, p. 50.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-009"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-009-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("98.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Stubbs, p. 370.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-008"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-008-backlink"
    }
  }, [_c('span', {
    staticClass: "nl2"
  }), _vm._v("99.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fowl, p. 216.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-007"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-007-backlink"
    }
  }, [_vm._v("100.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fowl, p. 209.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-006"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-006-backlink"
    }
  }, [_vm._v("101.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, pp. 89–90.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-005"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-005-backlink"
    }
  }, [_vm._v("102.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fowl, p. 90 (in reference to Phil. 2:2, 5).")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-004"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-004-backlink"
    }
  }, [_vm._v("103.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Fee, p. 416.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-003"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-003-backlink"
    }
  }, [_vm._v("104.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Oakes, "), _c('em', {
    staticClass: "italic"
  }, [_vm._v("Philippians")]), _vm._v(", p. 99. See comment on 1:28.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-002"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-002-backlink"
    }
  }, [_vm._v("105.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Cohick, p. 19. She notes the tendency of privileged readers to ‘spiritualize the suffering motif’ of the New Testament.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-001"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-001-backlink"
    }
  }, [_vm._v("106.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Keown II, p. 241; see comment at 3:17.")])]), _vm._v(" "), _c('div', {
    staticClass: "_idFootnote",
    attrs: {
      "epub:type": "footnote",
      "id": "footnote-000"
    }
  }, [_c('p', {
    staticClass: "fn"
  }, [_c('span', {
    staticClass: "fnot_num"
  }, [_c('a', {
    staticClass: "_idFootnoteAnchor _idGenColorInherit",
    attrs: {
      "href": "#/9781789742671_EPUB10#footnote-000-backlink"
    }
  }, [_vm._v("107.")])]), _c('span', {
    staticClass: "nl2"
  }), _vm._v("Eastman, ‘Imitating Christ’, p. 445.")])])])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-40a64828","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/9781789742671_EPUB10.vue
// module id = 81
// module chunks = 0